import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  addOrganizationMember,
  addBlacklistBulk,
  addToBlacklist,
  addToWhitelist,
  addWhitelistBulk,
  bulkManageDevices,
  cloneProfile,
  createClassroomSchedule,
  createManagedDeviceEnrollment,
  createOrganizationInvitation,
  createProfile,
  createPrompt,
  createTemporaryOverride,
  deleteClassroomSchedule,
  deleteProfile,
  deletePrompt,
  deleteTemporaryOverride,
  downloadOrganizationAuditCsv,
  downloadOrganizationReportCsv,
  downloadOrganizationSupportBundle,
  extractApiError,
  getActiveClassroomSchedules,
  getBlacklist,
  getCurrentPrompt,
  getManagedDevices,
  getOrganization,
  getOrganizationAuditLogs,
  getOrganizationInvitations,
  getOrganizationMembers,
  getOrganizationOpsSummary,
  getOrganizationReportEvents,
  getOrganizationReportSummary,
  getProfiles,
  getPrompts,
  getRosteringIntegration,
  getTemporaryOverrides,
  getUser,
  getWhitelist,
  importManagedDeviceRoster,
  leaveOrganization,
  normalizeDuplicateUserEmails,
  removeFromBlacklist,
  removeFromWhitelist,
  removeOrganizationMember,
  revokeOrganizationInvitation,
  revokeManagedDevice,
  rotateManagedDeviceToken,
  sendOrganizationSmtpTest,
  setCurrentPrompt,
  runOrganizationCleanup,
  syncRosteringIntegration,
  transferOrganizationOwnership,
  updateManagedDevice,
  updateClassroomSchedule,
  updateOrganization,
  updateOrganizationMember,
  updateRosteringIntegration,
  updateBlacklist,
  updatePrompt,
  updateWhitelist,
} from '../apiService';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DevicesIcon from '@mui/icons-material/Devices';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import RuleIcon from '@mui/icons-material/Rule';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { Link as RouterLink } from 'react-router-dom';
import PromptList from './PromptList';
import WhiteList from './WhiteList';
import BlackList from './BlackList';
import FeedbackForm from '../FeedbackForm';
import { UserContext } from '../UserContext';
import { formatOrganizationRole } from '../roleLabels';

const SCHOOL_POLICY_TEMPLATES = [
  {
    name: 'Elementary Classroom',
    description: 'A cautious baseline for younger students with broad instructional allowances.',
    prompts: [
      'Allow websites that directly support classroom learning, district curriculum, reading practice, math practice, or teacher-assigned activities. Block entertainment, social media, and unrelated browsing.',
      'If a website is ambiguous, prefer blocking unless it clearly supports elementary instruction or school-assigned research.',
    ],
    whitelist: ['clever.com', 'classlink.com', 'khanacademy.org'],
    blacklist: ['youtube.com', 'tiktok.com', 'reddit.com'],
  },
  {
    name: 'Library Research',
    description: 'Supports supervised research while still filtering common distraction destinations.',
    prompts: [
      'Allow academic research, reference sources, library databases, museums, archives, and school-approved productivity tools. Block entertainment, gaming, shopping, and social media.',
    ],
    whitelist: ['britannica.com', 'wikipedia.org', 'smithsonianmag.com'],
    blacklist: ['instagram.com', 'x.com', 'netflix.com'],
  },
  {
    name: 'Testing Mode',
    description: 'A tighter profile for assessment windows and focused exam sessions.',
    prompts: [
      'Only allow school-approved assessment tools, district systems, and essential authentication pages. Block open web research, AI assistants, media, messaging, and entertainment.',
    ],
    whitelist: ['collegeboard.org', 'google.com'],
    blacklist: ['chat.openai.com', 'claude.ai', 'discord.com'],
  },
];

const SCHEDULE_DAY_OPTIONS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const SURFACE_RADIUS = '14px';
const INNER_RADIUS = '10px';
const SURFACE_SHADOW = '0 1px 2px rgba(11, 46, 67, 0.04), 0 8px 24px -12px rgba(11, 46, 67, 0.08)';
const SURFACE_BORDER = '1px solid #E2EAF0';
const SURFACE_BACKGROUND = '#ffffff';
const PANEL_SX = {
  borderRadius: SURFACE_RADIUS,
  boxShadow: SURFACE_SHADOW,
  border: SURFACE_BORDER,
  background: SURFACE_BACKGROUND,
};
const INSET_PANEL_SX = {
  borderRadius: INNER_RADIUS,
  border: '1px solid #E6EDF2',
  backgroundColor: '#F8FBFD',
};
const ACCORDION_SX = {
  ...PANEL_SX,
  boxShadow: 'none',
  overflow: 'hidden',
  '&:before': {
    display: 'none',
  },
};
const SECTION_ANCHOR_SX = {
  scrollMarginTop: '88px',
};
const STAT_LABEL_SX = {
  color: '#52677A',
  fontWeight: 700,
  fontSize: '0.74rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};
const STAT_VALUE_SX = {
  color: '#0B2E43',
  fontWeight: 800,
  fontSize: '1.85rem',
  lineHeight: 1.15,
  mt: 0.75,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '-0.02em',
};
const SELECTED_PROFILE_STORAGE_KEY = 'dashboardSelectedProfileId';
const DASHBOARD_PAGE_IDS = ['overview', 'setup', 'policy', 'devices', 'reports', 'organization', 'operations'];
const DASHBOARD_SIDEBAR_COLLAPSED_KEY = 'dashboardSidebarCollapsed';
const ROSTER_CSV_TEMPLATE = [
  'label,student_name,student_email,student_id,classes,teachers',
  'Avery Chromebook,Avery Johnson,avery@example.edu,S1001,"Film 2A; English 3","Ms. Rivera; Mr. Lee"',
].join('\n');
const getItemId = (item) => item?._id || item?.id || '';

function getInitialDashboardPage() {
  const hashPage = window.location.hash.replace('#', '');
  if (hashPage === 'scope' || hashPage === 'schedules') {
    return 'setup';
  }
  if (hashPage === 'advanced') {
    return 'organization';
  }
  return DASHBOARD_PAGE_IDS.includes(hashPage) ? hashPage : 'overview';
}

function getInitialSidebarCollapsed() {
  return localStorage.getItem(DASHBOARD_SIDEBAR_COLLAPSED_KEY) === 'true';
}

function parseRosterList(value) {
  return (value || '')
    .split(/[,\n;]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatTemporaryOverrideExpiry(expiresAt) {
  const expiresMs = Date.parse(expiresAt || '');
  if (!Number.isFinite(expiresMs)) {
    return 'Expires soon';
  }

  const diffMs = expiresMs - Date.now();
  if (diffMs <= 0) {
    return 'Expired';
  }

  const diffMinutes = Math.round(diffMs / (60 * 1000));
  if (diffMinutes < 60) {
    return `Expires in ${diffMinutes} min`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `Expires in ${diffHours} hr`;
  }

  const diffDays = Math.round(diffHours / 24);
  return `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
}

function getDeviceTokenTiming(expiresAt) {
  if (!expiresAt) {
    return null;
  }
  const expiresMs = Date.parse(expiresAt);
  if (!Number.isFinite(expiresMs)) {
    return null;
  }
  const diffMs = expiresMs - Date.now();
  const diffDays = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
  return {
    expiresMs,
    diffMs,
    diffDays,
    isExpired: diffMs <= 0,
    isExpiringSoon: diffMs > 0 && diffDays <= 14,
  };
}

function formatReportSource(source) {
  const labels = {
    ai_policy: 'AI policy',
    allowlist: 'Allowlist',
    blocklist: 'Blocklist',
    default_allow: 'Default allow',
    low_signal_allow: 'Low signal allow',
    temporary_override: 'Temporary allow',
  };
  return labels[source] || String(source || 'Unknown source').replace(/_/g, ' ');
}

function formatReportUrl(event) {
  const currentUrl = (event?.current_url || '').trim();
  if (!currentUrl) {
    return event?.domain || 'Unknown page';
  }

  try {
    const url = new URL(currentUrl);
    const host = url.hostname.replace(/^www\./, '');
    const path = `${url.pathname || ''}${url.search || ''}`.replace(/\/$/, '');
    const label = `${host}${path && path !== '/' ? path : ''}`;
    return label.length > 96 ? `${label.slice(0, 93)}...` : label;
  } catch (error) {
    return currentUrl.length > 96 ? `${currentUrl.slice(0, 93)}...` : currentUrl;
  }
}

function getReportActorLabel(event) {
  return event?.student_name || event?.student_email || event?.device_label || event?.username || '';
}

function getReportActorDetails(event, actorLabel) {
  return [
    event?.student_name && event?.student_email ? event.student_email : '',
    event?.student_id,
    event?.device_label && event.device_label !== actorLabel ? event.device_label : '',
  ].filter(Boolean);
}

function Dashboard() {
  const { user, setUser } = useContext(UserContext);
  const [profiles, setProfiles] = useState([]);
  const [profilesHydrated, setProfilesHydrated] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(() => localStorage.getItem(SELECTED_PROFILE_STORAGE_KEY) || '');
  const [activeDashboardPage, setActiveDashboardPage] = useState(getInitialDashboardPage);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);
  const [prompts, setPrompts] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [temporaryOverrides, setTemporaryOverrides] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [newPromptText, setNewPromptText] = useState('');
  const [newWhitelistUrl, setNewWhitelistUrl] = useState('');
  const [newBlacklistUrl, setNewBlacklistUrl] = useState('');
  const [newTemporaryOverrideUrl, setNewTemporaryOverrideUrl] = useState('');
  const [newTemporaryOverrideMinutes, setNewTemporaryOverrideMinutes] = useState(60);
  const [newTemporaryOverrideNote, setNewTemporaryOverrideNote] = useState('');
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [editingPromptText, setEditingPromptText] = useState('');
  const [editingWhitelistId, setEditingWhitelistId] = useState(null);
  const [editingWhitelistUrl, setEditingWhitelistUrl] = useState('');
  const [editingBlacklistId, setEditingBlacklistId] = useState(null);
  const [editingBlacklistUrl, setEditingBlacklistUrl] = useState('');
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizationRole, setOrganizationRole] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationDomain, setOrganizationDomain] = useState('');
  const [institutionType, setInstitutionType] = useState('district');
  const [deploymentMethod, setDeploymentMethod] = useState('manual');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [staffOverride, setStaffOverride] = useState(false);
  const [organizationNotes, setOrganizationNotes] = useState('');
  const [rosteringIntegration, setRosteringIntegration] = useState(null);
  const [rosteringPresets, setRosteringPresets] = useState([]);
  const [rosteringProvider, setRosteringProvider] = useState('oneroster');
  const [rosteringSyncMode, setRosteringSyncMode] = useState('oneroster_api');
  const [rosteringBaseUrl, setRosteringBaseUrl] = useState('');
  const [rosteringAuthType, setRosteringAuthType] = useState('bearer');
  const [rosteringTokenUrl, setRosteringTokenUrl] = useState('');
  const [rosteringScope, setRosteringScope] = useState('');
  const [rosteringAccessToken, setRosteringAccessToken] = useState('');
  const [rosteringClientId, setRosteringClientId] = useState('');
  const [rosteringClientSecret, setRosteringClientSecret] = useState('');
  const [rosteringAutoProfiles, setRosteringAutoProfiles] = useState(true);
  const [rosteringAutoDevices, setRosteringAutoDevices] = useState(true);
  const [rosteringAutoTeachers, setRosteringAutoTeachers] = useState(true);
  const [rosteringDefaultCodeDays, setRosteringDefaultCodeDays] = useState(14);
  const [rosteringSyncSummary, setRosteringSyncSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [managedDevices, setManagedDevices] = useState([]);
  const [deviceLoadError, setDeviceLoadError] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [bulkDeviceProfileId, setBulkDeviceProfileId] = useState('');
  const [deviceStatusFilter, setDeviceStatusFilter] = useState('all');
  const [editingManagedDevice, setEditingManagedDevice] = useState(null);
  const [editingDeviceLabel, setEditingDeviceLabel] = useState('');
  const [editingDeviceStudentName, setEditingDeviceStudentName] = useState('');
  const [editingDeviceStudentEmail, setEditingDeviceStudentEmail] = useState('');
  const [editingDeviceStudentId, setEditingDeviceStudentId] = useState('');
  const [editingDeviceGroupName, setEditingDeviceGroupName] = useState('');
  const [editingDeviceClassNames, setEditingDeviceClassNames] = useState('');
  const [editingDeviceTeacherName, setEditingDeviceTeacherName] = useState('');
  const [editingDeviceTeacherNames, setEditingDeviceTeacherNames] = useState('');
  const [editingDeviceAssignedProfileId, setEditingDeviceAssignedProfileId] = useState('');
  const [opsSummary, setOpsSummary] = useState(null);
  const [reportSummary, setReportSummary] = useState(null);
  const [reportEvents, setReportEvents] = useState([]);
  const [reportDays, setReportDays] = useState(30);
  const [reportDecisionFilter, setReportDecisionFilter] = useState('');
  const [reportSourceFilter, setReportSourceFilter] = useState('');
  const [reportQuery, setReportQuery] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('org_admin');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [deviceLabel, setDeviceLabel] = useState('');
  const [deviceStudentName, setDeviceStudentName] = useState('');
  const [deviceStudentEmail, setDeviceStudentEmail] = useState('');
  const [deviceStudentId, setDeviceStudentId] = useState('');
  const [deviceGroupName, setDeviceGroupName] = useState('');
  const [deviceClassNames, setDeviceClassNames] = useState('');
  const [deviceTeacherName, setDeviceTeacherName] = useState('');
  const [deviceTeacherNames, setDeviceTeacherNames] = useState('');
  const [deviceAssignedProfileId, setDeviceAssignedProfileId] = useState('');
  const [deviceExpiresInDays, setDeviceExpiresInDays] = useState(7);
  const [managedDeviceMessage, setManagedDeviceMessage] = useState(null);
  const [rosterImportSummary, setRosterImportSummary] = useState(null);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneProfileName, setCloneProfileName] = useState('');
  const [activeClassroomSchedules, setActiveClassroomSchedules] = useState([]);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [scheduleClassName, setScheduleClassName] = useState('');
  const [scheduleTeacherName, setScheduleTeacherName] = useState('');
  const [scheduleRoom, setScheduleRoom] = useState('');
  const [scheduleDays, setScheduleDays] = useState('mon, tue, wed, thu, fri');
  const [scheduleStartTime, setScheduleStartTime] = useState('08:00');
  const [scheduleEndTime, setScheduleEndTime] = useState('08:50');
  const [scheduleMode, setScheduleMode] = useState('scheduled');
  const [scheduleTeacherLaunchEnabled, setScheduleTeacherLaunchEnabled] = useState(false);
  const [scheduleTimezone, setScheduleTimezone] = useState('America/Los_Angeles');
  const [scheduleStatus, setScheduleStatus] = useState('active');
  const [scheduleNotes, setScheduleNotes] = useState('');
  const [scheduleTargetGroupName, setScheduleTargetGroupName] = useState('');
  const [scheduleTargetTeacherName, setScheduleTargetTeacherName] = useState('');
  const [scheduleTargetStudentName, setScheduleTargetStudentName] = useState('');
  const [scheduleTargetDeviceLabel, setScheduleTargetDeviceLabel] = useState('');

  const whitelistFileInput = useRef(null);
  const blacklistFileInput = useRef(null);
  const rosterFileInput = useRef(null);

  useEffect(() => {
    const syncDashboardPage = () => setActiveDashboardPage(getInitialDashboardPage());
    window.addEventListener('hashchange', syncDashboardPage);
    return () => window.removeEventListener('hashchange', syncDashboardPage);
  }, []);

  useEffect(() => {
    localStorage.setItem(DASHBOARD_SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? 'true' : 'false');
  }, [sidebarCollapsed]);

  const currentScopeLabel = useMemo(() => {
    if (!selectedProfile) {
      return 'District baseline';
    }
    return profiles.find((profile) => getItemId(profile) === selectedProfile)?.profile_name || 'Policy profile';
  }, [profiles, selectedProfile]);

  const selectedProfileData = useMemo(
    () => profiles.find((profile) => getItemId(profile) === selectedProfile) || null,
    [profiles, selectedProfile]
  );

  const filteredManagedDevices = useMemo(() => {
    if (deviceStatusFilter === 'all') {
      return managedDevices;
    }
    return managedDevices.filter((device) => device.status === deviceStatusFilter);
  }, [deviceStatusFilter, managedDevices]);

  const allVisibleDevicesSelected =
    filteredManagedDevices.length > 0 &&
    filteredManagedDevices.every((device) => selectedDeviceIds.includes(device.id));

  const selectedProfileSchedules = selectedProfileData?.classroom_schedules || [];
  const selectedRosteringPreset = useMemo(
    () => rosteringPresets.find((preset) => preset.id === rosteringProvider) || null,
    [rosteringPresets, rosteringProvider]
  );
  const rosteringChecklistText = useMemo(() => {
    const sourceName = selectedRosteringPreset?.name || 'your SIS / roster provider';
    return [
      `Blockweb roster automation request for ${sourceName}`,
      '',
      'Please provide the rostering API connection details for Blockweb:',
      `1. Provider/source: ${sourceName}`,
      `2. Recommended sync type: ${selectedRosteringPreset?.sync_mode === 'clever_api' ? 'Clever API' : 'OneRoster API'}`,
      '3. API base URL/root endpoint',
      '4. Credential type: bearer token, OAuth2 client credentials, or basic credentials',
      '5. Token URL and scope, if OAuth2 client credentials are used',
      '6. Confirm Blockweb may read students, teachers, classes/sections, and enrollments',
      '',
      'Blockweb will use this to create/update student roster rows and class profile shells. District baseline rules still apply automatically.',
    ].join('\n');
  }, [selectedRosteringPreset]);

  const loadScopeData = useCallback(async (profileId = '') => {
    const [promptData, whitelistData, blacklistData, temporaryOverrideData] = await Promise.all([
      getPrompts(profileId || undefined),
      getWhitelist(profileId || undefined),
      getBlacklist(profileId || undefined),
      getTemporaryOverrides(profileId || undefined).catch(() => ({ overrides: [] })),
    ]);

    setPrompts(promptData);
    setWhitelist(whitelistData);
    setBlacklist(blacklistData);
    setTemporaryOverrides(temporaryOverrideData?.overrides || []);
  }, []);

  const applyRosteringIntegrationState = useCallback((data) => {
    const integration = data?.integration || {};
    setRosteringIntegration(integration);
    setRosteringPresets(data?.presets || []);
    setRosteringProvider(integration.provider || 'oneroster');
    setRosteringSyncMode(integration.sync_mode || 'oneroster_api');
    setRosteringBaseUrl(integration.base_url || '');
    setRosteringAuthType(integration.auth_type || 'bearer');
    setRosteringTokenUrl(integration.token_url || '');
    setRosteringScope(integration.scope || '');
    setRosteringAutoProfiles(integration.auto_create_profiles !== false);
    setRosteringAutoDevices(integration.auto_create_devices !== false);
    setRosteringAutoTeachers(integration.auto_create_teachers !== false);
    setRosteringDefaultCodeDays(integration.default_code_expires_in_days || 14);
    setRosteringSyncSummary(integration.last_sync_summary || null);
  }, []);

  const refreshManagedDeviceData = useCallback(async () => {
    try {
      const [deviceData, opsData, reportData] = await Promise.all([
        getManagedDevices(),
        getOrganizationOpsSummary().catch(() => null),
        getOrganizationReportSummary(reportDays).catch(() => null),
      ]);
      setManagedDevices(Array.isArray(deviceData?.devices) ? deviceData.devices : []);
      setOpsSummary(opsData);
      setReportSummary(reportData);
      setDeviceLoadError('');
      return deviceData;
    } catch (error) {
      setDeviceLoadError(extractApiError(error, 'Unable to load enrolled devices.'));
      return null;
    }
  }, [reportDays]);

  const refreshEverything = useCallback(async () => {
    const [profileData, currentPrompt, organizationData, rosteringData, memberData, auditData, invitationData, deviceData, opsData, reportData, activeSchedulesData] = await Promise.all([
      getProfiles().catch(() => []),
      getCurrentPrompt().catch(() => null),
      getOrganization().catch(() => null),
      getRosteringIntegration().catch(() => null),
      getOrganizationMembers().catch(() => ({ members: [] })),
      getOrganizationAuditLogs(30).catch(() => ({ logs: [] })),
      getOrganizationInvitations().catch(() => ({ invitations: [] })),
      getManagedDevices().catch((error) => ({ devices: null, error })),
      getOrganizationOpsSummary().catch(() => null),
      getOrganizationReportSummary(reportDays).catch(() => null),
      getActiveClassroomSchedules().catch(() => ({ active_schedules: [] })),
    ]);

    setProfiles(profileData);
    setProfilesHydrated(true);
    setOrganization(organizationData?.organization || null);
    setOrganizationRole(organizationData?.role || '');
    setOrganizationName(organizationData?.organization?.name || '');
    setOrganizationDomain(organizationData?.organization?.domain || '');
    setInstitutionType(organizationData?.organization?.institution_type || 'district');
    setDeploymentMethod(organizationData?.organization?.deployment_method || 'manual');
    setContactName(organizationData?.organization?.contact_name || '');
    setContactEmail(organizationData?.organization?.contact_email || '');
    setStaffOverride(Boolean(organizationData?.organization?.allow_staff_override));
    setOrganizationNotes(organizationData?.organization?.notes || '');
    applyRosteringIntegrationState(rosteringData);
    setMembers(memberData?.members || []);
    setAuditLogs(auditData?.logs || []);
    setInvitations(invitationData?.invitations || []);
    if (Array.isArray(deviceData?.devices)) {
      setManagedDevices(deviceData.devices);
      setDeviceLoadError('');
    } else if (deviceData?.error) {
      setDeviceLoadError(extractApiError(deviceData.error, 'Unable to load enrolled devices.'));
    }
    setOpsSummary(opsData);
    setReportSummary(reportData);
    setActiveClassroomSchedules(activeSchedulesData?.active_schedules || []);
    if (currentPrompt?.id) {
      setSelectedPromptId(currentPrompt.id);
    }

    await loadScopeData(selectedProfile);
  }, [applyRosteringIntegrationState, loadScopeData, reportDays, selectedProfile]);

  useEffect(() => {
    refreshEverything();
  }, [refreshEverything]);

  useEffect(() => {
    const refreshVisibleDeviceData = () => {
      if (!document.hidden) {
        refreshManagedDeviceData();
      }
    };

    window.addEventListener('focus', refreshVisibleDeviceData);
    document.addEventListener('visibilitychange', refreshVisibleDeviceData);
    const intervalId = window.setInterval(refreshVisibleDeviceData, 15000);

    return () => {
      window.removeEventListener('focus', refreshVisibleDeviceData);
      document.removeEventListener('visibilitychange', refreshVisibleDeviceData);
      window.clearInterval(intervalId);
    };
  }, [refreshManagedDeviceData]);

  useEffect(() => {
    loadScopeData(selectedProfile);
  }, [loadScopeData, selectedProfile]);

  useEffect(() => {
    resetScheduleForm();
  }, [selectedProfile]);

  useEffect(() => {
    if (!selectedProfile) {
      localStorage.removeItem(SELECTED_PROFILE_STORAGE_KEY);
      return;
    }
    localStorage.setItem(SELECTED_PROFILE_STORAGE_KEY, selectedProfile);
  }, [selectedProfile]);

  useEffect(() => {
    if (!profilesHydrated || !selectedProfile) {
      return;
    }
    if (!profiles.some((profile) => getItemId(profile) === selectedProfile)) {
      setSelectedProfile('');
    }
  }, [profiles, profilesHydrated, selectedProfile]);

  useEffect(() => {
    getOrganizationReportEvents({
      days: reportDays,
      decision: reportDecisionFilter || undefined,
      source: reportSourceFilter || undefined,
      query: reportQuery || undefined,
      limit: 100,
    })
      .then((data) => setReportEvents(data.events || []))
      .catch(() => setReportEvents([]));
  }, [reportDays, reportDecisionFilter, reportSourceFilter, reportQuery]);

  useEffect(() => {
    setSelectedDeviceIds((current) => current.filter((id) => managedDevices.some((device) => device.id === id)));
  }, [managedDevices]);

  const clearEditors = () => {
    setEditingPromptId(null);
    setEditingPromptText('');
    setEditingWhitelistId(null);
    setEditingWhitelistUrl('');
    setEditingBlacklistId(null);
    setEditingBlacklistUrl('');
  };

  function resetScheduleForm() {
    setEditingScheduleId(null);
    setScheduleClassName('');
    setScheduleTeacherName('');
    setScheduleRoom('');
    setScheduleDays('mon, tue, wed, thu, fri');
    setScheduleStartTime('08:00');
    setScheduleEndTime('08:50');
    setScheduleMode('scheduled');
    setScheduleTeacherLaunchEnabled(false);
    setScheduleTimezone('America/Los_Angeles');
    setScheduleStatus('active');
    setScheduleNotes('');
    setScheduleTargetGroupName('');
    setScheduleTargetTeacherName('');
    setScheduleTargetStudentName('');
    setScheduleTargetDeviceLabel('');
  }

  const parseScheduleDays = () =>
    scheduleDays
      .split(',')
      .map((day) => day.trim().toLowerCase().slice(0, 3))
      .filter(Boolean);

  const showStatus = (severity, text) => {
    setStatusMessage({ severity, text });
  };

  const showApiError = (error, fallback) => {
    showStatus('error', extractApiError(error, fallback));
  };

  const showManagedDeviceMessage = (severity, text) => {
    setManagedDeviceMessage({ severity, text });
  };

  const toggleDeviceSelection = (deviceId) => {
    setSelectedDeviceIds((current) =>
      current.includes(deviceId) ? current.filter((id) => id !== deviceId) : [...current, deviceId]
    );
  };

  const clearDeviceSelection = () => {
    setSelectedDeviceIds([]);
    setBulkDeviceProfileId('');
  };

  const toggleVisibleDeviceSelection = () => {
    if (filteredManagedDevices.length === 0) {
      return;
    }

    if (allVisibleDevicesSelected) {
      setSelectedDeviceIds((current) =>
        current.filter((id) => !filteredManagedDevices.some((device) => device.id === id))
      );
      return;
    }

    setSelectedDeviceIds((current) => [
      ...new Set([...current, ...filteredManagedDevices.map((device) => device.id)]),
    ]);
  };

  const handleCreateProfile = async () => {
    if (!profileName.trim()) {
      return;
    }

    const createdProfile = await createProfile(profileName.trim());
    setProfiles((current) => [...current, createdProfile]);
    setSelectedProfile(getItemId(createdProfile));
    setProfileDialogOpen(false);
    setProfileName('');
    showStatus('success', `Created policy profile "${createdProfile.profile_name}".`);
  };

  const openCloneDialog = () => {
    const currentName = profiles.find((profile) => getItemId(profile) === selectedProfile)?.profile_name || 'Policy profile';
    setCloneProfileName(`Copy of ${currentName}`);
    setCloneDialogOpen(true);
  };

  const refreshOrganizationData = async () => {
    const [organizationData, rosteringData, memberData, auditData, invitationData, deviceData, opsData, reportData] = await Promise.all([
      getOrganization(),
      getRosteringIntegration().catch(() => null),
      getOrganizationMembers(),
      getOrganizationAuditLogs(30),
      getOrganizationInvitations(),
      getManagedDevices().catch((error) => ({ devices: null, error })),
      getOrganizationOpsSummary(),
      getOrganizationReportSummary(reportDays),
    ]);
    setOrganization(organizationData.organization);
    setOrganizationRole(organizationData.role || '');
    setOrganizationName(organizationData.organization.name);
    setOrganizationDomain(organizationData.organization.domain || '');
    setInstitutionType(organizationData.organization.institution_type || 'district');
    setDeploymentMethod(organizationData.organization.deployment_method || 'manual');
    setContactName(organizationData.organization.contact_name || '');
    setContactEmail(organizationData.organization.contact_email || '');
    setStaffOverride(Boolean(organizationData.organization.allow_staff_override));
    setOrganizationNotes(organizationData.organization.notes || '');
    applyRosteringIntegrationState(rosteringData);
    setMembers(memberData.members);
    setAuditLogs(auditData.logs);
    setInvitations(invitationData.invitations);
    if (Array.isArray(deviceData.devices)) {
      setManagedDevices(deviceData.devices);
      setDeviceLoadError('');
    } else if (deviceData.error) {
      setDeviceLoadError(extractApiError(deviceData.error, 'Unable to load enrolled devices.'));
    }
    setOpsSummary(opsData);
    setReportSummary(reportData);
  };

  const handleDeleteProfile = async () => {
    if (!selectedProfile) {
      return;
    }

    await deleteProfile(selectedProfile);
    setProfiles((current) => current.filter((profile) => getItemId(profile) !== selectedProfile));
    setSelectedProfile('');
    resetScheduleForm();
    showStatus('success', 'Deleted the selected policy profile.');
  };

  const handleAddPrompt = async (event) => {
    event.preventDefault();
    if (!newPromptText.trim()) {
      return;
    }

    const createdPrompt = await createPrompt(newPromptText.trim(), prompts.length, selectedProfile || undefined);
    setPrompts((current) => [...current, createdPrompt]);
    setNewPromptText('');
    const createdPromptId = getItemId(createdPrompt);
    setSelectedPromptId(createdPromptId);
    await setCurrentPrompt(createdPromptId, createdPrompt.text);
  };

  const handleUpdatePrompt = async (id, order, event) => {
    event.preventDefault();
    if (!editingPromptText.trim()) {
      return;
    }

    const updatedPrompt = await updatePrompt(id, editingPromptText.trim(), order);
    setPrompts((current) => current.map((prompt) => (getItemId(prompt) === id ? updatedPrompt : prompt)));
    clearEditors();
  };

  const handleDeletePrompt = async (id) => {
    await deletePrompt(id);
    setPrompts((current) => current.filter((prompt) => getItemId(prompt) !== id));
    if (selectedPromptId === id) {
      setSelectedPromptId(null);
    }
  };

  const handlePromptSelect = async (id, text) => {
    setSelectedPromptId(id);
    await setCurrentPrompt(id, text);
  };

  const handleAddToWhitelist = async (event) => {
    event.preventDefault();
    if (!newWhitelistUrl.trim()) {
      return;
    }

    const createdUrl = await addToWhitelist(newWhitelistUrl.trim(), selectedProfile || undefined);
    setWhitelist((current) => [...current, createdUrl]);
    setNewWhitelistUrl('');
  };

  const handleAddToBlacklist = async (event) => {
    event.preventDefault();
    if (!newBlacklistUrl.trim()) {
      return;
    }

    const createdUrl = await addToBlacklist(newBlacklistUrl.trim(), selectedProfile || undefined);
    setBlacklist((current) => [...current, createdUrl]);
    setNewBlacklistUrl('');
  };

  const handleAddTemporaryOverride = async (event) => {
    event.preventDefault();
    if (!newTemporaryOverrideUrl.trim()) {
      return;
    }

    const response = await createTemporaryOverride(
      {
        url: newTemporaryOverrideUrl.trim(),
        expires_in_minutes: Number(newTemporaryOverrideMinutes) || 60,
        note: newTemporaryOverrideNote.trim() || undefined,
      },
      selectedProfile || undefined
    );

    setTemporaryOverrides((current) => [...current, response.override]);
    setNewTemporaryOverrideUrl('');
    setNewTemporaryOverrideMinutes(60);
    setNewTemporaryOverrideNote('');
    showStatus('success', `Quick allow added for ${currentScopeLabel}.`);
  };

  const handlePauseFiltering = async () => {
    const response = await createTemporaryOverride(
      {
        url: '*',
        expires_in_minutes: Number(newTemporaryOverrideMinutes) || 60,
        note: newTemporaryOverrideNote.trim() || `Filtering paused for ${currentScopeLabel}`,
      },
      selectedProfile || undefined
    );

    setTemporaryOverrides((current) => [...current, response.override]);
    setNewTemporaryOverrideMinutes(60);
    setNewTemporaryOverrideNote('');
    showStatus('success', `Filtering paused for ${currentScopeLabel}.`);
  };

  const handleDeleteTemporaryOverride = async (overrideId) => {
    await deleteTemporaryOverride(overrideId);
    setTemporaryOverrides((current) => current.filter((item) => item.id !== overrideId));
  };

  const handleRemoveFromWhitelist = async (id) => {
    await removeFromWhitelist(id);
    setWhitelist((current) => current.filter((item) => item._id !== id));
  };

  const handleRemoveFromBlacklist = async (id) => {
    await removeFromBlacklist(id);
    setBlacklist((current) => current.filter((item) => item._id !== id));
  };

  const handleUpdateWhitelist = async (id, event) => {
    event.preventDefault();
    const existing = whitelist.find((item) => item._id === id);
    if (!existing || !editingWhitelistUrl.trim()) {
      return;
    }

    const updated = await updateWhitelist(id, editingWhitelistUrl.trim(), existing.order);
    setWhitelist((current) => current.map((item) => (item._id === id ? updated : item)));
    clearEditors();
  };

  const handleUpdateBlacklist = async (id, event) => {
    event.preventDefault();
    const existing = blacklist.find((item) => item._id === id);
    if (!existing || !editingBlacklistUrl.trim()) {
      return;
    }

    const updated = await updateBlacklist(id, editingBlacklistUrl.trim(), existing.order);
    setBlacklist((current) => current.map((item) => (item._id === id ? updated : item)));
    clearEditors();
  };

  const readFileLines = async (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const urls = String(event.target.result)
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((url) => ({ url }));
        resolve(urls);
      };
      reader.readAsText(file);
    });

  const handleWhitelistFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const urls = await readFileLines(file);

    if (selectedProfile) {
      await Promise.all(urls.map((entry) => addToWhitelist(entry.url, selectedProfile)));
    } else {
      await addWhitelistBulk(urls);
    }

    await loadScopeData(selectedProfile);
    showStatus('success', `Imported ${urls.length} allowlist entries into ${currentScopeLabel}.`);
  };

  const handleBlacklistFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const urls = await readFileLines(file);

    if (selectedProfile) {
      await Promise.all(urls.map((entry) => addToBlacklist(entry.url, selectedProfile)));
    } else {
      await addBlacklistBulk(urls);
    }

    await loadScopeData(selectedProfile);
    showStatus('success', `Imported ${urls.length} blocklist entries into ${currentScopeLabel}.`);
  };

  const reorderList = async (type, items) => {
    if (type === 'prompts') {
      const reordered = await Promise.all(
        items.map((item, index) => updatePrompt(item._id, item.text, index))
      );
      setPrompts(reordered);
      return;
    }

    if (type === 'whitelist') {
      const reordered = await Promise.all(
        items.map((item, index) => updateWhitelist(item._id, item.url, index))
      );
      setWhitelist(reordered);
      return;
    }

    const reordered = await Promise.all(
      items.map((item, index) => updateBlacklist(item._id, item.url, index))
    );
    setBlacklist(reordered);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const listMap = {
      prompts: [prompts, setPrompts],
      whitelist: [whitelist, setWhitelist],
      blacklist: [blacklist, setBlacklist],
    };

    const [items, setItems] = listMap[result.type];
    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setItems(reordered);
    await reorderList(result.type, reordered);
  };

  const handleOrganizationRename = async (event) => {
    event.preventDefault();
    if (!organizationName.trim()) {
      return;
    }
    const response = await updateOrganization({
      name: organizationName.trim(),
      domain: organizationDomain.trim(),
      institution_type: institutionType,
      deployment_method: deploymentMethod,
      contact_name: contactName.trim(),
      contact_email: contactEmail.trim(),
      allow_staff_override: staffOverride,
      notes: organizationNotes.trim(),
    });
    setOrganization(response.organization);
    showStatus('success', 'Organization details updated.');
    await refreshOrganizationData();
  };

  const handleSaveRosteringIntegration = async () => {
    try {
      const response = await updateRosteringIntegration({
        provider: rosteringProvider,
        sync_mode: rosteringSyncMode,
        base_url: rosteringBaseUrl.trim(),
        auth_type: rosteringAuthType,
        token_url: rosteringTokenUrl.trim() || undefined,
        scope: rosteringScope.trim() || undefined,
        access_token: rosteringAccessToken.trim() || undefined,
        client_id: rosteringClientId.trim() || undefined,
        client_secret: rosteringClientSecret || undefined,
        enabled: true,
        auto_create_profiles: rosteringAutoProfiles,
        auto_create_devices: rosteringAutoDevices,
        auto_create_teachers: rosteringAutoTeachers,
        auto_create_schedules: false,
        default_code_expires_in_days: Number(rosteringDefaultCodeDays) || 14,
      });
      applyRosteringIntegrationState({ integration: response.integration, presets: rosteringPresets });
      setRosteringAccessToken('');
      setRosteringClientSecret('');
      showStatus('success', 'Rostering integration saved.');
    } catch (error) {
      showApiError(error, 'Unable to save rostering integration.');
    }
  };

  const handleSyncRosteringIntegration = async (dryRun = false) => {
    try {
      const response = await syncRosteringIntegration({ dry_run: dryRun });
      setRosteringSyncSummary(response.summary);
      showStatus(
        'success',
        dryRun
          ? `Rostering dry run found ${response.summary.received_count} student record(s).`
          : `Rostering sync complete. Created ${response.summary.created_count}, updated ${response.summary.updated_count}.`
      );
      await refreshEverything();
    } catch (error) {
      showApiError(error, 'Unable to sync roster data.');
    }
  };

  const handleAddMember = async (event) => {
    event.preventDefault();
    if (!memberEmail.trim()) {
      return;
    }
    await addOrganizationMember(memberEmail.trim(), memberRole);
    setMemberEmail('');
    setMemberRole('org_admin');
    showStatus('success', 'Organization member updated.');
    await refreshOrganizationData();
  };

  const handleMemberRoleChange = async (memberId, role) => {
    await updateOrganizationMember(memberId, role);
    showStatus('success', 'Member role updated.');
    await refreshOrganizationData();
  };

  const handleRemoveMember = async (memberId) => {
    await removeOrganizationMember(memberId);
    showStatus('success', 'Member removed from the workspace.');
    await refreshOrganizationData();
  };

  const handleCreateInvitation = async (event) => {
    event.preventDefault();
    if (!inviteEmail.trim()) {
      return;
    }

    const response = await createOrganizationInvitation(inviteEmail.trim(), inviteRole);
    setInviteEmail('');
    setInviteRole('viewer');
    showStatus('success', response.email_sent ? 'Invitation created and emailed.' : 'Invitation created. Email delivery is not configured on this server.');
    await refreshOrganizationData();
  };

  const handleCloneProfile = async () => {
    if (!selectedProfile || !cloneProfileName.trim()) {
      return;
    }
    const cloned = await cloneProfile(selectedProfile, cloneProfileName.trim());
    setProfiles((current) => [...current, cloned]);
    setCloneDialogOpen(false);
    setCloneProfileName('');
    showStatus('success', `Cloned profile into "${cloned.profile_name}".`);
  };

  const handleApplyTemplate = async (template) => {
    const profileId = selectedProfile || undefined;
    await Promise.all([
      ...template.prompts.map((text, index) => createPrompt(text, prompts.length + index, profileId)),
      ...template.whitelist.map((url) => addToWhitelist(url, profileId)),
      ...template.blacklist.map((url) => addToBlacklist(url, profileId)),
    ]);
    await loadScopeData(selectedProfile);
    showStatus('success', `Applied the ${template.name} starter template to ${currentScopeLabel}.`);
  };

  const handleRevokeInvitation = async (invitationId) => {
    await revokeOrganizationInvitation(invitationId);
    showStatus('success', 'Invitation revoked.');
    await refreshOrganizationData();
  };

  const handleResendInvitation = async (invitation) => {
    const response = await createOrganizationInvitation(invitation.email, invitation.role);
    showStatus('success', response.email_sent ? 'Invitation email resent.' : 'Invitation already exists, but email delivery is not configured on this server.');
    await refreshOrganizationData();
  };

  const copyToClipboard = async (text, onSuccess, onError) => {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess?.();
    } catch (error) {
      onError?.();
    }
  };

  const handleCopyInvitationLink = async (link) => {
    await copyToClipboard(
      link,
      () => showStatus('success', 'Invitation link copied.'),
      () => showStatus('error', 'Unable to copy the invitation link on this browser.')
    );
  };

  const handleCopyRosterTemplate = async () => {
    await copyToClipboard(
      ROSTER_CSV_TEMPLATE,
      () => showManagedDeviceMessage('success', 'Roster CSV template copied. Paste it into a spreadsheet, then export CSV.'),
      () => showManagedDeviceMessage('error', 'Unable to copy the roster template on this browser.')
    );
  };

  const handleCopyRosteringChecklist = async () => {
    await copyToClipboard(
      rosteringChecklistText,
      () => showManagedDeviceMessage('success', 'Rostering checklist copied for your IT/developer contact.'),
      () => showManagedDeviceMessage('error', 'Unable to copy the rostering checklist on this browser.')
    );
  };

  const handleCreateManagedDevice = async (event) => {
    event.preventDefault();
    if (!deviceLabel.trim()) {
      return;
    }

    try {
      const response = await createManagedDeviceEnrollment({
        label: deviceLabel.trim(),
        student_name: deviceStudentName.trim() || undefined,
        student_email: deviceStudentEmail.trim() || undefined,
        student_id: deviceStudentId.trim() || undefined,
        group_name: deviceGroupName.trim() || undefined,
        class_names: parseRosterList(deviceClassNames),
        teacher_name: deviceTeacherName.trim() || undefined,
        teacher_names: parseRosterList(deviceTeacherNames),
        assigned_profile_id: deviceAssignedProfileId || undefined,
        expires_in_days: Number(deviceExpiresInDays) || 7,
      });

      setDeviceLabel('');
      setDeviceStudentName('');
      setDeviceStudentEmail('');
      setDeviceStudentId('');
      setDeviceGroupName('');
      setDeviceClassNames('');
      setDeviceTeacherName('');
      setDeviceTeacherNames('');
      setDeviceAssignedProfileId('');
      setDeviceExpiresInDays(7);
      setRosterImportSummary(null);
      setManagedDevices((current) => {
        const createdDevice = response?.device;
        if (!createdDevice?.id || current.some((device) => device.id === createdDevice.id)) {
          return current;
        }
        return [createdDevice, ...current];
      });
      await copyToClipboard(
        response.device.enrollment_code,
        () => showManagedDeviceMessage('success', `Enrollment code copied: ${response.device.enrollment_code}`),
        () => showManagedDeviceMessage('warning', `Enrollment code created: ${response.device.enrollment_code}`)
      );
      await refreshOrganizationData();
    } catch (error) {
      setManagedDeviceMessage(null);
      showApiError(error, 'Unable to create managed device enrollment code.');
    }
  };

  const handleRevokeManagedDevice = async (deviceId) => {
    try {
      await revokeManagedDevice(deviceId);
      showStatus('success', 'Managed device revoked.');
      await refreshOrganizationData();
    } catch (error) {
      showApiError(error, 'Unable to revoke the managed device.');
    }
  };

  const handleOpenManagedDeviceEditor = (device) => {
    setEditingManagedDevice(device);
    setEditingDeviceLabel(device.label || '');
    setEditingDeviceStudentName(device.student_name || '');
    setEditingDeviceStudentEmail(device.student_email || '');
    setEditingDeviceStudentId(device.student_id || '');
    setEditingDeviceGroupName(device.group_name || '');
    setEditingDeviceClassNames((device.class_names || []).join(', '));
    setEditingDeviceTeacherName(device.teacher_name || '');
    setEditingDeviceTeacherNames((device.teacher_names || []).join(', '));
    setEditingDeviceAssignedProfileId(device.assigned_profile_id || '');
  };

  const handleCloseManagedDeviceEditor = () => {
    setEditingManagedDevice(null);
    setEditingDeviceLabel('');
    setEditingDeviceStudentName('');
    setEditingDeviceStudentEmail('');
    setEditingDeviceStudentId('');
    setEditingDeviceGroupName('');
    setEditingDeviceClassNames('');
    setEditingDeviceTeacherName('');
    setEditingDeviceTeacherNames('');
    setEditingDeviceAssignedProfileId('');
  };

  const handleSaveManagedDevice = async () => {
    if (!editingManagedDevice || !editingDeviceLabel.trim()) {
      showStatus('warning', 'Add a device label before saving.');
      return;
    }

    try {
      await updateManagedDevice(editingManagedDevice.id, {
        label: editingDeviceLabel.trim(),
        student_name: editingDeviceStudentName.trim(),
        student_email: editingDeviceStudentEmail.trim(),
        student_id: editingDeviceStudentId.trim(),
        group_name: editingDeviceGroupName.trim(),
        class_names: parseRosterList(editingDeviceClassNames),
        teacher_name: editingDeviceTeacherName.trim(),
        teacher_names: parseRosterList(editingDeviceTeacherNames),
        assigned_profile_id: editingDeviceAssignedProfileId || '',
      });
      handleCloseManagedDeviceEditor();
      showStatus('success', 'Managed device updated.');
      await refreshOrganizationData();
    } catch (error) {
      showApiError(error, 'Unable to update the managed device.');
    }
  };

  const handleRotateManagedDevice = async (deviceId) => {
    try {
      const response = await rotateManagedDeviceToken(deviceId);
      if (response?.device?.enrollment_code) {
        await copyToClipboard(
          response.device.enrollment_code,
          () => showManagedDeviceMessage('success', `Device token rotated. New enrollment code copied: ${response.device.enrollment_code}`),
          () => showManagedDeviceMessage('warning', `Device token rotated. New enrollment code: ${response.device.enrollment_code}`)
        );
      } else {
        showManagedDeviceMessage('success', 'Device token rotated. The old session is invalid and the new enrollment code is ready.');
      }
      await refreshOrganizationData();
    } catch (error) {
      setManagedDeviceMessage(null);
      showApiError(error, 'Unable to rotate the managed device token.');
    }
  };

  const handleRosterFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const csvText = await file.text();
      const response = await importManagedDeviceRoster({
        csv_text: csvText,
        assigned_profile_id: deviceAssignedProfileId || undefined,
        expires_in_days: Number(deviceExpiresInDays) || 7,
        update_existing: true,
      });
      setRosterImportSummary(response);
      showStatus(
        'success',
        `Roster import complete. Created ${response.created_count}, updated ${response.updated_count}, skipped ${response.skipped_rows.length}.`
      );
      await refreshOrganizationData();
    } catch (error) {
      showApiError(error, 'Unable to import the managed device roster.');
    } finally {
      event.target.value = '';
    }
  };

  const handleBulkDeviceAction = async (action) => {
    if (selectedDeviceIds.length === 0) {
      showStatus('warning', 'Select at least one managed device first.');
      return;
    }

    try {
      const payload = { action, device_ids: selectedDeviceIds };
      if (action === 'assign_profile') {
        payload.assigned_profile_id = bulkDeviceProfileId || undefined;
      }
      await bulkManageDevices(payload);
      showStatus('success', `Bulk device action "${action.replace('_', ' ')}" completed.`);
      clearDeviceSelection();
      await refreshOrganizationData();
    } catch (error) {
      showApiError(error, 'Unable to complete the bulk device action.');
    }
  };

  const handleOrganizationCleanup = async (action) => {
    try {
      const response = await runOrganizationCleanup({ action, stale_after_days: 7 });
      showStatus('success', `${response.message}. Affected: ${response.affected_count}.`);
      await refreshOrganizationData();
    } catch (error) {
      showApiError(error, 'Unable to run the cleanup action.');
    }
  };

  const handleNormalizeDuplicateEmails = async () => {
    try {
      const response = await normalizeDuplicateUserEmails();
      showStatus('success', `Normalized ${response.updated_count} duplicate user email record(s).`);
      await refreshOrganizationData();
    } catch (error) {
      showApiError(error, 'Unable to normalize duplicate user emails.');
    }
  };

  const handleCopyOpsSnapshot = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(opsSummary || {}, null, 2));
      showStatus('success', 'Operations snapshot copied.');
    } catch (error) {
      showStatus('error', 'Unable to copy the operations snapshot on this browser.');
    }
  };

  const handleSendSmtpTest = async () => {
    try {
      const response = await sendOrganizationSmtpTest();
      showStatus('success', `SMTP test email sent to ${response.to_email}.`);
      await refreshOrganizationData();
    } catch (error) {
      showApiError(error, 'Unable to send the SMTP test email.');
    }
  };

  const handleExportReport = async () => {
    await downloadOrganizationReportCsv(reportDays);
    showStatus('success', 'Activity report export started.');
  };

  const handleExportAudit = async () => {
    await downloadOrganizationAuditCsv(5000);
    showStatus('success', 'Audit log export started.');
  };

  const handleExportSupportBundle = async () => {
    await downloadOrganizationSupportBundle();
    showStatus('success', 'Support bundle export started.');
  };

  const handleTransferOwnership = async (memberId) => {
    await transferOrganizationOwnership(memberId);
    const updatedUser = await getUser();
    setUser(updatedUser);
    showStatus('success', 'Ownership transferred.');
    await refreshOrganizationData();
  };

  const handleLeaveWorkspace = async () => {
    await leaveOrganization();
    const updatedUser = await getUser();
    setUser(updatedUser);
    showStatus('success', 'You left the workspace and were moved to a personal workspace.');
    await refreshEverything();
  };

  const handleSaveClassroomSchedule = async (event) => {
    event.preventDefault();
    if (!selectedProfile) {
      showStatus('warning', 'Select a policy profile before creating a classroom schedule.');
      return;
    }
    if (!scheduleClassName.trim()) {
      return;
    }

    const payload = {
      class_name: scheduleClassName.trim(),
      teacher_name: scheduleTeacherName.trim(),
      room: scheduleRoom.trim(),
      days_of_week: parseScheduleDays(),
      start_time: scheduleStartTime,
      end_time: scheduleEndTime,
      activation_mode: scheduleMode,
      teacher_launch_enabled: scheduleTeacherLaunchEnabled,
      timezone: scheduleTimezone.trim() || 'America/Los_Angeles',
      status: scheduleStatus,
      notes: scheduleNotes.trim(),
      target_group_name: scheduleTargetGroupName.trim(),
      target_teacher_name: scheduleTargetTeacherName.trim(),
      target_student_name: scheduleTargetStudentName.trim(),
      target_device_label: scheduleTargetDeviceLabel.trim(),
    };

    if (editingScheduleId) {
      await updateClassroomSchedule(selectedProfile, editingScheduleId, payload);
      showStatus('success', 'Classroom schedule updated.');
    } else {
      await createClassroomSchedule(selectedProfile, payload);
      showStatus('success', 'Classroom schedule created.');
    }

    resetScheduleForm();
    await refreshEverything();
  };

  const handleEditSchedule = (schedule) => {
    setEditingScheduleId(schedule.id);
    setScheduleClassName(schedule.class_name || '');
    setScheduleTeacherName(schedule.teacher_name || '');
    setScheduleRoom(schedule.room || '');
    setScheduleDays((schedule.days_of_week || []).join(', '));
    setScheduleStartTime(schedule.start_time || '08:00');
    setScheduleEndTime(schedule.end_time || '08:50');
    setScheduleMode(schedule.activation_mode || 'scheduled');
    setScheduleTeacherLaunchEnabled(Boolean(schedule.teacher_launch_enabled));
    setScheduleTimezone(schedule.timezone || 'America/Los_Angeles');
    setScheduleStatus(schedule.status || 'active');
    setScheduleNotes(schedule.notes || '');
    setScheduleTargetGroupName(schedule.target_group_name || '');
    setScheduleTargetTeacherName(schedule.target_teacher_name || '');
    setScheduleTargetStudentName(schedule.target_student_name || '');
    setScheduleTargetDeviceLabel(schedule.target_device_label || '');
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!selectedProfile) {
      return;
    }
    await deleteClassroomSchedule(selectedProfile, scheduleId);
    if (editingScheduleId === scheduleId) {
      resetScheduleForm();
    }
    showStatus('success', 'Classroom schedule deleted.');
    await refreshEverything();
  };

  const dashboardNavItems = [
    {
      id: 'overview',
      label: 'Home',
      shortLabel: 'Home',
      helper: 'Rollout status',
      description: 'Rollout progress and the next setup task.',
      icon: <HomeOutlinedIcon fontSize="small" />,
    },
    {
      id: 'setup',
      label: 'Profiles',
      shortLabel: 'Profiles',
      helper: `${profiles.length} custom`,
      description: 'Choose the baseline or a class profile, then manage templates and class-period schedules.',
      icon: <LayersOutlinedIcon fontSize="small" />,
    },
    {
      id: 'policy',
      label: 'Rules',
      shortLabel: 'Rules',
      helper: `${prompts.length} review`,
      description: 'Write the AI review rules and maintain temporary allows plus persistent allow/block lists.',
      icon: <RuleIcon fontSize="small" />,
    },
    {
      id: 'devices',
      label: 'Devices',
      shortLabel: 'Devices',
      helper: `${managedDevices.length} enrolled`,
      description: 'Set up roster sync, CSV import, or a single test enrollment code.',
      icon: <DevicesIcon fontSize="small" />,
    },
    {
      id: 'reports',
      label: 'Reports',
      shortLabel: 'Reports',
      helper: `${reportSummary?.summary?.total_scans || 0} scans`,
      description: 'Review filtering activity, decision sources, and export reports after rollout.',
      icon: <AssessmentIcon fontSize="small" />,
    },
    {
      id: 'organization',
      label: 'Staff',
      shortLabel: 'Staff',
      helper: `${members.length} member${members.length === 1 ? '' : 's'}`,
      description: 'Manage workspace settings, staff access, invitations, and product feedback.',
      icon: <PeopleAltIcon fontSize="small" />,
    },
    {
      id: 'operations',
      label: 'Ops',
      shortLabel: 'Ops',
      helper: opsSummary?.status || 'Checking',
      description: 'Check deployment health, run cleanup tools, and inspect audit history.',
      icon: <SettingsSuggestIcon fontSize="small" />,
    },
  ];

  const activeDashboardItem =
    dashboardNavItems.find((item) => item.id === activeDashboardPage) || dashboardNavItems[0];

  const handleDashboardPageChange = (pageId) => {
    setActiveDashboardPage(pageId);
    window.history.replaceState(null, '', `#${pageId}`);
  };

  const setupSteps = [
    {
      label: 'Baseline',
      done: prompts.length > 0,
      helper: prompts.length > 0 ? `${prompts.length} review rule${prompts.length === 1 ? '' : 's'} active` : 'Add at least one rule',
      page: 'policy',
    },
    {
      label: 'Profiles',
      done: profiles.length > 0,
      helper: profiles.length > 0 ? `${profiles.length} classroom profile${profiles.length === 1 ? '' : 's'} ready` : 'Optional classroom layers',
      page: 'setup',
    },
    {
      label: 'Devices',
      done: managedDevices.length > 0,
      helper:
        managedDevices.length > 0
          ? `${managedDevices.length} managed device${managedDevices.length === 1 ? '' : 's'} in workspace`
          : 'Create your first code',
      page: 'devices',
    },
    {
      label: 'Reports',
      done: (reportSummary?.summary?.total_scans || 0) > 0,
      helper:
        (reportSummary?.summary?.total_scans || 0) > 0
          ? `${reportSummary?.summary?.total_scans || 0} scans recorded`
          : 'Review activity after rollout',
      page: 'reports',
    },
  ];

  const reportOverview = useMemo(() => {
    const summary = reportSummary?.summary || {};
    const total = summary.total_scans || 0;
    const allowed = summary.allowed || 0;
    const blocked = summary.blocked || 0;
    const decidedTotal = allowed + blocked;
    const allowedPct = decidedTotal ? Math.round((allowed / decidedTotal) * 100) : 0;
    const blockedPct = decidedTotal ? 100 - allowedPct : 0;
    const blockRate = total ? Math.round((blocked / total) * 100) : 0;
    const topDomain = reportSummary?.top_domains?.[0] || null;
    const topBlockedDomain = reportSummary?.blocked_domains?.[0] || null;
    const mixedDomains = reportSummary?.mixed_decision_domains || [];
    const sources = Object.entries(reportSummary?.by_source || {})
      .map(([source, count]) => ({ label: formatReportSource(source), count }))
      .sort((a, b) => b.count - a.count);

    return {
      total,
      allowed,
      blocked,
      allowedPct,
      blockedPct,
      blockRate,
      topDomain,
      topBlockedDomain,
      mixedDomains,
      sources,
    };
  }, [reportSummary]);

  const hasReportActivity = (reportSummary?.summary?.total_scans || 0) > 0 || reportEvents.length > 0;
  const nextSetupStep = setupSteps.find((step) => !step.done);
  const pageIcon = activeDashboardItem.icon;
  const completedSetupSteps = setupSteps.filter((step) => step.done).length;
  const setupProgress = Math.round((completedSetupSteps / setupSteps.length) * 100);

  return (
    <Container maxWidth={false} disableGutters sx={{ py: { xs: 1.25, md: 2 }, px: { xs: 1.25, md: 2.5 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: sidebarCollapsed ? '76px minmax(0, 1fr)' : '244px minmax(0, 1fr)',
          },
          gap: { xs: 1.5, md: 2.5 },
          alignItems: 'start',
          transition: 'grid-template-columns 180ms ease',
        }}
      >
        <Box
          component="aside"
          sx={{
            position: { xs: 'static', md: 'sticky' },
            top: { md: 72 },
            zIndex: 1,
            p: { xs: 1.25, md: sidebarCollapsed ? 1 : 1.5 },
            borderRadius: '16px',
            background:
              'radial-gradient(circle at 90% -10%, rgba(19,148,127,0.28), transparent 38%), linear-gradient(178deg, #0C2A3C 0%, #0F3650 100%)',
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '0 2px 4px rgba(11,46,67,0.08), 0 24px 48px -24px rgba(11,46,67,0.4)',
            minHeight: { md: 'calc(100vh - 92px)' },
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            display: { md: 'flex' },
            flexDirection: { md: 'column' },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: 'space-between', md: sidebarCollapsed ? 'center' : 'space-between' }}
            spacing={1}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ minWidth: 0, display: { xs: 'flex', md: sidebarCollapsed ? 'none' : 'flex' } }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'linear-gradient(140deg, #16A38C 0%, #0F7E66 100%)',
                  color: 'white',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  boxShadow: '0 4px 12px -4px rgba(19,148,127,0.5)',
                }}
              >
                {(organization?.name || 'S').charAt(0).toUpperCase()}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#ffffff', fontWeight: 750, fontSize: '0.9rem', lineHeight: 1.2 }}>
                  Admin Console
                </Typography>
                <Typography
                  sx={{
                    color: '#9FB6C6',
                    mt: 0.3,
                    lineHeight: 1.3,
                    fontSize: '0.76rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {organization?.name || 'School workspace'}
                </Typography>
              </Box>
            </Stack>
            <IconButton
              size="small"
              onClick={() => setSidebarCollapsed((value) => !value)}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.14)',
                backgroundColor: 'rgba(255,255,255,0.05)',
                color: '#D7E3EB',
                display: { xs: 'none', md: 'inline-flex' },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
              }}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Stack>
          <Chip
            size="small"
            label={currentScopeLabel}
            sx={{
              mt: 1.5,
              maxWidth: '100%',
              bgcolor: 'rgba(19,148,127,0.18)',
              color: '#8FE5D3',
              border: '1px solid rgba(19,148,127,0.32)',
              fontWeight: 700,
              display: { xs: 'inline-flex', md: sidebarCollapsed ? 'none' : 'inline-flex' },
            }}
          />
          <Typography
            sx={{
              display: { xs: 'none', md: sidebarCollapsed ? 'none' : 'block' },
              mt: 2,
              mb: 0.75,
              px: 1,
              color: 'rgba(255,255,255,0.35)',
              fontWeight: 800,
              fontSize: '0.64rem',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
            }}
          >
            Workspace
          </Typography>
          <Stack
            spacing={{ xs: 0.75, md: 0.5 }}
            sx={{
              mt: { xs: 1.5, md: 0 },
              flexDirection: { xs: 'row', md: 'column' },
              overflowX: { xs: 'auto', md: 'visible' },
              width: '100%',
              minWidth: 0,
              pb: { xs: 0.5, md: 0 },
            }}
          >
            {dashboardNavItems.map((item) => {
              const isActive = item.id === activeDashboardPage;
              return (
                <Button
                  key={item.id}
                  onClick={() => handleDashboardPageChange(item.id)}
                  aria-label={sidebarCollapsed ? `${item.label}: ${item.helper}` : undefined}
                  variant="text"
                  sx={{
                    justifyContent: { xs: 'space-between', md: sidebarCollapsed ? 'center' : 'space-between' },
                    flexShrink: 0,
                    minWidth: { xs: 150, md: sidebarCollapsed ? 44 : 'auto' },
                    minHeight: { xs: 42, md: 40 },
                    borderRadius: '10px',
                    px: { xs: 1.25, md: sidebarCollapsed ? 0 : 1 },
                    py: 0.75,
                    textTransform: 'none',
                    color: isActive ? '#ffffff' : '#B9C9D5',
                    background: isActive
                      ? 'linear-gradient(140deg, rgba(22,163,140,0.95) 0%, rgba(15,126,102,0.95) 100%)'
                      : 'transparent',
                    border: '1px solid transparent',
                    boxShadow: isActive ? '0 8px 18px -8px rgba(19,148,127,0.65), inset 0 1px 0 rgba(255,255,255,0.14)' : 'none',
                    transition: 'background-color 160ms ease, color 160ms ease, transform 160ms ease',
                    '&:hover': {
                      background: isActive
                        ? 'linear-gradient(140deg, rgba(22,163,140,1) 0%, rgba(15,126,102,1) 100%)'
                        : 'rgba(255,255,255,0.07)',
                      color: '#ffffff',
                      transform: isActive ? 'none' : 'translateX(2px)',
                    },
                  }}
                >
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.15, minWidth: 0 }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: '7px',
                        flexShrink: 0,
                        backgroundColor: isActive ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.06)',
                        color: isActive ? '#ffffff' : '#C8D7E1',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        textAlign: 'left',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        display: { xs: 'inline', md: sidebarCollapsed ? 'none' : 'inline' },
                      }}
                    >
                      {item.label}
                    </Box>
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      ml: 2,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: isActive ? 'rgba(255,255,255,0.85)' : '#7E96A7',
                      display: { xs: 'inline', md: sidebarCollapsed ? 'none' : 'inline' },
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.helper}
                  </Box>
                </Button>
              );
            })}
          </Stack>
          <Box
            sx={{
              display: { xs: 'none', md: sidebarCollapsed ? 'none' : 'block' },
              mt: 'auto',
              pt: 2,
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(255,255,255,0.04)',
              }}
            >
              <Typography sx={{ color: '#D7E3EB', fontWeight: 700, fontSize: '0.78rem' }}>
                Need rollout help?
              </Typography>
              <Typography sx={{ color: '#8AA2B3', fontSize: '0.74rem', mt: 0.4, lineHeight: 1.5 }}>
                We help schools design their first policy.
              </Typography>
              <Button
                size="small"
                fullWidth
                component={RouterLink}
                to="/contact"
                sx={{
                  mt: 1.25,
                  color: '#8FE5D3',
                  border: '1px solid rgba(19,148,127,0.4)',
                  bgcolor: 'rgba(19,148,127,0.12)',
                  '&:hover': { bgcolor: 'rgba(19,148,127,0.22)' },
                }}
              >
                Contact Support
              </Button>
            </Box>
          </Box>
        </Box>

        <Stack component="main" spacing={2} sx={{ minWidth: 0 }}>
          <Box sx={{ px: 0.5, pt: { xs: 0.5, md: 1 }, pb: 0.5 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(140deg, #0E4D6E 0%, #13947F 130%)',
                  color: '#ffffff',
                  flexShrink: 0,
                  boxShadow: '0 10px 22px -10px rgba(14,77,110,0.5)',
                }}
              >
                {pageIcon}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="h3" sx={{ color: '#13293A', fontSize: { xs: '1.4rem', md: '1.6rem' } }}>
                  {activeDashboardItem.label}
                </Typography>
                <Typography sx={{ color: '#52677A', mt: 0.35, maxWidth: 820, lineHeight: 1.5, fontSize: '0.92rem' }}>
                  {activeDashboardItem.description}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={currentScopeLabel}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  bgcolor: 'rgba(19,148,127,0.1)',
                  color: '#0C6E5E',
                  border: '1px solid rgba(19,148,127,0.22)',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              />
            </Stack>
          </Box>

          {statusMessage && <Alert severity={statusMessage.severity}>{statusMessage.text}</Alert>}
          {deviceLoadError && ['overview', 'devices', 'operations'].includes(activeDashboardPage) && (
            <Alert severity="warning">{deviceLoadError}</Alert>
          )}

          <Stack key={activeDashboardPage} spacing={2} sx={{ animation: 'viewEnter 320ms ease both' }}>

          {activeDashboardPage === 'overview' && (
            <Box id="overview" sx={SECTION_ANCHOR_SX}>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  {[
                    { label: 'Enrolled devices', value: managedDevices.length, icon: <DevicesIcon fontSize="small" />, accent: '#0E4D6E', tint: 'rgba(14,77,110,0.1)', page: 'devices' },
                    { label: 'Scans this window', value: reportSummary?.summary?.total_scans || 0, icon: <AssessmentIcon fontSize="small" />, accent: '#13947F', tint: 'rgba(19,148,127,0.1)', page: 'reports' },
                    { label: 'Review rules', value: prompts.length, icon: <RuleIcon fontSize="small" />, accent: '#B07A1F', tint: 'rgba(223,162,58,0.16)', page: 'policy' },
                    { label: 'Live schedule blocks', value: activeClassroomSchedules.length, icon: <LayersOutlinedIcon fontSize="small" />, accent: '#46729B', tint: 'rgba(70,114,155,0.1)', page: 'setup' },
                  ].map((kpi) => (
                    <Grid item xs={6} lg={3} key={kpi.label}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleDashboardPageChange(kpi.page)}
                        sx={{
                          ...PANEL_SX,
                          p: 2,
                          height: '100%',
                          width: '100%',
                          textAlign: 'left',
                          font: 'inherit',
                          cursor: 'pointer',
                          transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            borderColor: `${kpi.accent}55`,
                            boxShadow: `0 14px 28px -18px ${kpi.accent}88`,
                          },
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ ...STAT_LABEL_SX, fontSize: '0.7rem' }}>{kpi.label}</Typography>
                            <Typography sx={{ ...STAT_VALUE_SX, color: kpi.accent }}>{kpi.value}</Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: '10px',
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: kpi.tint,
                              color: kpi.accent,
                              flexShrink: 0,
                            }}
                          >
                            {kpi.icon}
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ ...PANEL_SX, overflow: 'hidden' }}>
                  <Stack spacing={0}>
                    <Stack
                      direction={{ xs: 'column', lg: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', lg: 'center' }}
                      sx={{ p: { xs: 1.75, md: 2 }, borderBottom: SURFACE_BORDER }}
                    >
                      <Box>
                        <Typography variant="h5" sx={{ color: '#13293A', mb: 0.5, fontWeight: 750, fontSize: '1.05rem' }}>
                          Workspace rollout
                        </Typography>
                        <Typography sx={{ color: '#52677A', lineHeight: 1.55 }}>
                          Baseline applies to everyone. Classroom profiles and schedules add instructional exceptions on top.
                        </Typography>
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <Chip
                          label={`${completedSetupSteps}/${setupSteps.length} complete`}
                          sx={{ bgcolor: 'rgba(11,46,67,0.07)', color: '#456E80', fontWeight: 800 }}
                        />
                        {nextSetupStep ? (
                          <Button variant="contained" onClick={() => handleDashboardPageChange(nextSetupStep.page)}>
                            Continue Setup
                          </Button>
                        ) : (
                          <Chip label="Pilot checklist complete" sx={{ bgcolor: 'rgba(19,148,127,0.14)', color: '#0C6E5E', fontWeight: 800 }} />
                        )}
                      </Stack>
                    </Stack>

                    <Box sx={{ px: { xs: 1.75, md: 2 }, pt: 1.75 }}>
                      <LinearProgress
                        variant="determinate"
                        value={setupProgress}
                        sx={{
                          height: 8,
                          borderRadius: '999px',
                          bgcolor: 'rgba(11,46,67,0.08)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: '999px',
                            bgcolor: '#13947F',
                          },
                        }}
                      />
                    </Box>

                    <Grid container spacing={2.5} sx={{ p: { xs: 1.75, md: 2.5 } }}>
                      <Grid item xs={12} lg={8}>
                        <Typography sx={{ ...STAT_LABEL_SX, mb: 1.25 }}>Setup modules</Typography>
                        <Box sx={{ overflow: 'hidden', backgroundColor: '#ffffff', border: SURFACE_BORDER, borderRadius: INNER_RADIUS }}>
                          <Stack divider={<Divider flexItem />}>
                            {setupSteps.map((step, stepIndex) => (
                              <Box
                                key={step.label}
                                component="button"
                                type="button"
                                onClick={() => handleDashboardPageChange(step.page)}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: 'auto 1fr', sm: 'auto minmax(0, 1fr) auto 24px' },
                                  alignItems: 'center',
                                  gap: 1.5,
                                  width: '100%',
                                  p: 1.75,
                                  border: 0,
                                  textAlign: 'left',
                                  font: 'inherit',
                                  cursor: 'pointer',
                                  backgroundColor: '#ffffff',
                                  transition: 'background-color 140ms ease',
                                  '&:hover': {
                                    backgroundColor: '#F8FBFD',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    display: 'grid',
                                    placeItems: 'center',
                                    flexShrink: 0,
                                    background: step.done
                                      ? 'linear-gradient(140deg, #16A38C 0%, #0F7E66 100%)'
                                      : '#EFF4F7',
                                    border: step.done ? 'none' : '1px solid #D7E2EA',
                                    color: step.done ? 'white' : '#52677A',
                                    fontWeight: 800,
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {step.done ? <CheckRoundedIcon sx={{ fontSize: 17 }} /> : stepIndex + 1}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ color: '#13293A', fontWeight: 750 }}>{step.label}</Typography>
                                  <Typography sx={{ color: '#52677A', mt: 0.35, lineHeight: 1.45, fontSize: '0.88rem' }}>{step.helper}</Typography>
                                </Box>
                                <Chip
                                  size="small"
                                  label={step.done ? 'Complete' : 'Open'}
                                  sx={{
                                    justifySelf: { xs: 'flex-start', sm: 'auto' },
                                    gridColumn: { xs: '2', sm: 'auto' },
                                    bgcolor: step.done ? 'rgba(19,148,127,0.12)' : '#EFF4F7',
                                    color: step.done ? '#0C6E5E' : '#4A6375',
                                    fontWeight: 700,
                                  }}
                                />
                                <Box
                                  component="span"
                                  sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#8CA3B2',
                                  }}
                                >
                                  <ChevronRightIcon fontSize="small" />
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                      <Grid item xs={12} lg={4}>
                        <Typography sx={{ ...STAT_LABEL_SX, mb: 1.25 }}>Up next</Typography>
                        <Stack spacing={1.5}>
                          {nextSetupStep ? (
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: INNER_RADIUS,
                                border: '1px solid rgba(19,148,127,0.32)',
                                background: 'linear-gradient(150deg, rgba(19,148,127,0.09) 0%, rgba(14,77,110,0.05) 100%)',
                              }}
                            >
                              <Typography sx={{ color: '#0C6E5E', fontWeight: 800, fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                                Recommended step
                              </Typography>
                              <Typography sx={{ color: '#13293A', fontWeight: 750, mt: 0.75, fontSize: '1.02rem' }}>
                                {nextSetupStep.label}
                              </Typography>
                              <Typography sx={{ color: '#52677A', mt: 0.4, lineHeight: 1.5, fontSize: '0.88rem' }}>
                                {nextSetupStep.helper}
                              </Typography>
                              <Button
                                size="small"
                                variant="contained"
                                sx={{ mt: 1.5 }}
                                endIcon={<ChevronRightIcon fontSize="small" />}
                                onClick={() => handleDashboardPageChange(nextSetupStep.page)}
                              >
                                Open {nextSetupStep.label}
                              </Button>
                            </Box>
                          ) : (
                            <Alert severity="success">Pilot checklist is complete.</Alert>
                          )}
                          <Box sx={{ border: SURFACE_BORDER, borderRadius: INNER_RADIUS, overflow: 'hidden', backgroundColor: '#ffffff' }}>
                            {[
                              { label: 'Review filtering activity', page: 'reports' },
                              { label: 'Manage enrolled devices', page: 'devices' },
                              { label: 'Invite staff to the workspace', page: 'organization' },
                            ].map((link, linkIndex) => (
                              <Box
                                key={link.page}
                                component="button"
                                type="button"
                                onClick={() => handleDashboardPageChange(link.page)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%',
                                  px: 1.75,
                                  py: 1.4,
                                  border: 0,
                                  borderTop: linkIndex === 0 ? 0 : '1px solid #EBF1F5',
                                  textAlign: 'left',
                                  font: 'inherit',
                                  cursor: 'pointer',
                                  backgroundColor: '#ffffff',
                                  color: '#3E5A6C',
                                  fontWeight: 650,
                                  fontSize: '0.9rem',
                                  transition: 'background-color 140ms ease, color 140ms ease',
                                  '&:hover': {
                                    backgroundColor: '#F8FBFD',
                                    color: '#0E4D6E',
                                  },
                                }}
                              >
                                {link.label}
                                <ChevronRightIcon sx={{ fontSize: 18, color: '#8CA3B2' }} />
                              </Box>
                            ))}
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          )}

          {activeDashboardPage === 'setup' && (
            <>

        <Card id="scope" sx={{ ...PANEL_SX, ...SECTION_ANCHOR_SX }}>
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack spacing={2.25}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
                <TextField
                  select
                  fullWidth
                  label="Editing scope"
                  value={selectedProfile}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (value) =>
                      value
                        ? profiles.find((profile) => getItemId(profile) === value)?.profile_name || 'Policy profile'
                        : 'District baseline (default)',
                  }}
                  onChange={(event) => {
                    clearEditors();
                    setSelectedProfile(event.target.value);
                  }}
                  sx={{ flex: 1 }}
                  helperText="District baseline applies to everyone. Custom profiles add classroom-specific layers on top."
                >
                  <MenuItem value="">District baseline (default)</MenuItem>
                  {profiles.map((profile) => (
                    <MenuItem key={getItemId(profile)} value={getItemId(profile)}>
                      {profile.profile_name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setProfileDialogOpen(true)} sx={{ minWidth: 190 }}>
                  New Profile
                </Button>
                <Button variant="outlined" onClick={openCloneDialog} disabled={!selectedProfile} sx={{ minWidth: 170 }}>
                  Clone Selected
                </Button>
                <Button variant="outlined" color="error" onClick={handleDeleteProfile} disabled={!selectedProfile} sx={{ minWidth: 170 }}>
                  Delete Selected
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Accordion id="schedules" defaultExpanded sx={{ ...ACCORDION_SX, ...SECTION_ANCHOR_SX }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box>
              <Typography variant="h5" sx={{ color: '#0B2E43' }}>
                Templates and schedules
              </Typography>
              <Typography sx={{ color: '#52677A', mt: 0.4 }}>
                Starter templates, class-period scheduling, and teacher-launched sessions.
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, md: 2.5 }, pt: 0 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="h6" sx={{ color: '#0B2E43', mb: 1 }}>
                    Starter templates
                  </Typography>
                  <Grid container spacing={2}>
                    {SCHOOL_POLICY_TEMPLATES.map((template) => (
                      <Grid item xs={12} md={4} key={template.name}>
                        <Box sx={{ ...INSET_PANEL_SX, p: 2.25, height: '100%' }}>
                          <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>{template.name}</Typography>
                          <Typography sx={{ color: '#52677A', mt: 0.9, lineHeight: 1.7 }}>
                            {template.description}
                          </Typography>
                          <Button sx={{ mt: 1.75 }} variant="outlined" onClick={() => handleApplyTemplate(template)}>
                            Apply to {currentScopeLabel}
                          </Button>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                  Classroom scheduling
                </Typography>
                <Typography sx={{ color: '#52677A', lineHeight: 1.75, mb: 2.5 }}>
                  Attach this profile to a class period. During matching class time, managed devices use this profile on top of District baseline.
                </Typography>
                {!selectedProfile ? (
                  <Alert severity="info">Select a policy profile first. Classroom schedules are attached to profiles, not the district baseline.</Alert>
                ) : (
                  <form onSubmit={handleSaveClassroomSchedule}>
                    <Stack spacing={1.5}>
                      <TextField
                        label="Class or session name"
                        value={scheduleClassName}
                        onChange={(event) => setScheduleClassName(event.target.value)}
                        fullWidth
                      />
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          label="Teacher"
                          value={scheduleTeacherName}
                          onChange={(event) => setScheduleTeacherName(event.target.value)}
                          fullWidth
                        />
                        <TextField
                          label="Room"
                          value={scheduleRoom}
                          onChange={(event) => setScheduleRoom(event.target.value)}
                          fullWidth
                        />
                      </Stack>
                      <TextField
                        label="Days"
                        value={scheduleDays}
                        onChange={(event) => setScheduleDays(event.target.value)}
                        helperText={`Use comma-separated day codes like ${SCHEDULE_DAY_OPTIONS.join(', ')}.`}
                        fullWidth
                      />
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          label="Target group"
                          value={scheduleTargetGroupName}
                          onChange={(event) => setScheduleTargetGroupName(event.target.value)}
                          fullWidth
                        />
                        <TextField
                          label="Target teacher"
                          value={scheduleTargetTeacherName}
                          onChange={(event) => setScheduleTargetTeacherName(event.target.value)}
                          fullWidth
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          label="Target student"
                          value={scheduleTargetStudentName}
                          onChange={(event) => setScheduleTargetStudentName(event.target.value)}
                          fullWidth
                        />
                        <TextField
                          label="Target device label"
                          value={scheduleTargetDeviceLabel}
                          onChange={(event) => setScheduleTargetDeviceLabel(event.target.value)}
                          fullWidth
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          label="Start time"
                          type="time"
                          value={scheduleStartTime}
                          onChange={(event) => setScheduleStartTime(event.target.value)}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          label="End time"
                          type="time"
                          value={scheduleEndTime}
                          onChange={(event) => setScheduleEndTime(event.target.value)}
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          select
                          label="Activation mode"
                          value={scheduleMode}
                          onChange={(event) => setScheduleMode(event.target.value)}
                          fullWidth
                        >
                          <MenuItem value="scheduled">Scheduled by class period</MenuItem>
                          <MenuItem value="teacher_launch">Teacher-launched session</MenuItem>
                        </TextField>
                        <TextField
                          select
                          label="Status"
                          value={scheduleStatus}
                          onChange={(event) => setScheduleStatus(event.target.value)}
                          fullWidth
                        >
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="paused">Paused</MenuItem>
                        </TextField>
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                          select
                          label="Teacher launch"
                          value={scheduleTeacherLaunchEnabled ? 'enabled' : 'disabled'}
                          onChange={(event) => setScheduleTeacherLaunchEnabled(event.target.value === 'enabled')}
                          fullWidth
                        >
                          <MenuItem value="disabled">Admin-only schedule</MenuItem>
                          <MenuItem value="enabled">Teacher can launch manually</MenuItem>
                        </TextField>
                        <TextField
                          label="Timezone"
                          value={scheduleTimezone}
                          onChange={(event) => setScheduleTimezone(event.target.value)}
                          fullWidth
                        />
                      </Stack>
                      <TextField
                        label="Notes"
                        value={scheduleNotes}
                        onChange={(event) => setScheduleNotes(event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                      />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <Button type="submit" variant="contained">
                          {editingScheduleId ? 'Update Classroom Schedule' : 'Create Classroom Schedule'}
                        </Button>
                        {editingScheduleId && (
                          <Button variant="outlined" onClick={resetScheduleForm}>
                            Cancel Edit
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                  </form>
                )}
              </Grid>
              <Grid item xs={12} lg={7}>
                <Typography variant="h5" sx={{ color: '#0B2E43', mb: 1 }}>
                  {selectedProfile ? `${selectedProfileData?.profile_name} schedule blocks` : 'Schedule blocks'}
                </Typography>
                <Stack spacing={1.5}>
                  {selectedProfile && selectedProfileSchedules.length === 0 && (
                    <Typography sx={{ color: '#7A8FA0' }}>
                      No classroom schedules on this profile yet.
                    </Typography>
                  )}
                  {selectedProfileSchedules.map((schedule) => (
                    <Box key={schedule.id} sx={{ ...INSET_PANEL_SX, p: 2 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                        <Box>
                          <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                            {schedule.class_name}
                          </Typography>
                          <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                            {(schedule.days_of_week || []).join(', ')} • {schedule.start_time} - {schedule.end_time}
                          </Typography>
                          <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                            {(schedule.teacher_name || 'No teacher set')} • {(schedule.room || 'No room set')} • {schedule.activation_mode}
                          </Typography>
                          {(schedule.target_group_name || schedule.target_teacher_name || schedule.target_student_name || schedule.target_device_label) && (
                            <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                              Targets: {[
                                schedule.target_group_name ? `Group ${schedule.target_group_name}` : null,
                                schedule.target_teacher_name ? `Teacher ${schedule.target_teacher_name}` : null,
                                schedule.target_student_name ? `Student ${schedule.target_student_name}` : null,
                                schedule.target_device_label ? `Device ${schedule.target_device_label}` : null,
                              ].filter(Boolean).join(' • ')}
                            </Typography>
                          )}
                          {schedule.notes && (
                            <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                              {schedule.notes}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <Button size="small" onClick={() => handleEditSchedule(schedule)}>
                            Edit
                          </Button>
                          <Button size="small" color="error" onClick={() => handleDeleteSchedule(schedule.id)}>
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="h6" sx={{ color: '#0B2E43', mb: 1 }}>
                    Active scheduled blocks right now
                  </Typography>
                  <Stack spacing={1.25}>
                    {activeClassroomSchedules.length === 0 && (
                      <Typography sx={{ color: '#7A8FA0' }}>
                        No scheduled classroom blocks are active at this moment.
                      </Typography>
                    )}
                    {activeClassroomSchedules.slice(0, 4).map((entry) => (
                      <Box key={`${entry.profile_id}-${entry.schedule.id}`} sx={{ ...INSET_PANEL_SX, p: 2 }}>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                          {entry.schedule.class_name} • {entry.profile_name}
                        </Typography>
                        <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                          {(entry.schedule.teacher_name || 'Unassigned teacher')} • {entry.schedule.start_time} - {entry.schedule.end_time} • {(entry.schedule.room || 'Room not set')}
                        </Typography>
                        {(entry.schedule.target_group_name || entry.schedule.target_teacher_name || entry.schedule.target_student_name || entry.schedule.target_device_label) && (
                          <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                            Targets: {[
                              entry.schedule.target_group_name ? `Group ${entry.schedule.target_group_name}` : null,
                              entry.schedule.target_teacher_name ? `Teacher ${entry.schedule.target_teacher_name}` : null,
                              entry.schedule.target_student_name ? `Student ${entry.schedule.target_student_name}` : null,
                              entry.schedule.target_device_label ? `Device ${entry.schedule.target_device_label}` : null,
                            ].filter(Boolean).join(' • ')}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
            </>
          )}

          {activeDashboardPage === 'policy' && (
        <Box id="policy" sx={SECTION_ANCHOR_SX}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Card sx={{ ...PANEL_SX, height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                  AI review rules
                </Typography>

                <form onSubmit={handleAddPrompt}>
                  <TextField
                    value={newPromptText}
                    onChange={(event) => setNewPromptText(event.target.value)}
                    label="Add a new review rule"
                    multiline
                    minRows={3}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleAddPrompt}>
                            <AddIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>

                <Box sx={{ mt: 2.5 }}>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="prompts" type="prompts">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          <PromptList
                            prompts={prompts}
                            activePromptId={selectedPromptId}
                            editingPromptId={editingPromptId}
                            editingText={editingPromptText}
                            onDelete={handleDeletePrompt}
                            onEditStart={(prompt) => {
                              setEditingPromptId(prompt._id);
                              setEditingPromptText(prompt.text);
                            }}
                            onEditChange={setEditingPromptText}
                            onEditSave={handleUpdatePrompt}
                            onEditCancel={clearEditors}
                            onSelect={handlePromptSelect}
                          />
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card sx={{ ...PANEL_SX, height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                  Quick controls
                </Typography>

                <Box sx={{ ...INSET_PANEL_SX, p: 2.25 }}>
                  <Typography variant="h6" sx={{ color: '#0B2E43', mb: 0.75 }}>
                    Quick allows and pauses
                  </Typography>
                  <form onSubmit={handleAddTemporaryOverride}>
                    <Stack spacing={1.5}>
                      <TextField
                        value={newTemporaryOverrideUrl}
                        onChange={(event) => setNewTemporaryOverrideUrl(event.target.value)}
                        label="URL or domain to allow for now"
                        fullWidth
                      />
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                          select
                          label="Duration"
                          value={newTemporaryOverrideMinutes}
                          onChange={(event) => setNewTemporaryOverrideMinutes(Number(event.target.value))}
                          sx={{ minWidth: 180 }}
                        >
                          <MenuItem value={15}>15 minutes</MenuItem>
                          <MenuItem value={60}>1 hour</MenuItem>
                          <MenuItem value={90}>Class period</MenuItem>
                          <MenuItem value={480}>School day</MenuItem>
                          <MenuItem value={1440}>24 hours</MenuItem>
                          <MenuItem value={10080}>7 days</MenuItem>
                        </TextField>
                        <TextField
                          value={newTemporaryOverrideNote}
                          onChange={(event) => setNewTemporaryOverrideNote(event.target.value)}
                          label="Optional note"
                          fullWidth
                        />
                        <Button type="submit" variant="contained">
                          Quick Allow
                        </Button>
                        <Button type="button" variant="outlined" color="warning" onClick={handlePauseFiltering}>
                          Pause Filtering
                        </Button>
                      </Stack>
                    </Stack>
                  </form>

                  <Stack spacing={1.25} sx={{ mt: 2 }}>
                    {temporaryOverrides.length === 0 && (
                      <Typography sx={{ color: '#7A8FA0' }}>
                        No temporary allows or pauses are active in {currentScopeLabel.toLowerCase()}.
                      </Typography>
                    )}
                    {temporaryOverrides.slice(0, 5).map((override) => (
                      <Box key={override.id} sx={{ borderTop: '1px solid rgba(11,46,67,0.08)', pt: 1.25 }}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                        >
                          <Box>
                            <Typography sx={{ color: '#0B2E43', fontWeight: 700, wordBreak: 'break-word' }}>
                              {override.url}
                            </Typography>
                            <Typography sx={{ color: '#52677A', mt: 0.4 }}>
                              {formatTemporaryOverrideExpiry(override.expires_at)}
                              {override.note ? ` • ${override.note}` : ''}
                            </Typography>
                          </Box>
                          <Button color="error" onClick={() => handleDeleteTemporaryOverride(override.id)}>
                            Remove
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Accordion defaultExpanded={false} sx={{ ...ACCORDION_SX, mt: 2, boxShadow: 'none' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>Hard allow and block lists</Typography>
                      <Typography sx={{ color: '#52677A', mt: 0.35 }}>
                        Persistent entries for trusted curriculum or permanent blocks.
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} xl={6}>
                        <Box sx={{ ...INSET_PANEL_SX, p: 2.25, height: '100%' }}>
                          <Typography variant="h6" sx={{ color: '#0B2E43', mb: 0.75 }}>
                            Allowlist
                          </Typography>
                          <form onSubmit={handleAddToWhitelist}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                              <TextField
                                value={newWhitelistUrl}
                                onChange={(event) => setNewWhitelistUrl(event.target.value)}
                                label="Add allowlist entry"
                                fullWidth
                              />
                              <Button type="submit" variant="contained">
                                Add
                              </Button>
                              <IconButton color="primary" onClick={() => whitelistFileInput.current?.click()}>
                                <AttachFileIcon />
                              </IconButton>
                            </Stack>
                            <input
                              ref={whitelistFileInput}
                              type="file"
                              accept=".csv,.txt"
                              onChange={handleWhitelistFileUpload}
                              style={{ display: 'none' }}
                            />
                          </form>

                          <Box sx={{ mt: 2 }}>
                            <DragDropContext onDragEnd={handleDragEnd}>
                              <Droppable droppableId="whitelist" type="whitelist">
                                {(provided) => (
                                  <div ref={provided.innerRef} {...provided.droppableProps}>
                                    <WhiteList
                                      whitelist={whitelist}
                                      editingId={editingWhitelistId}
                                      editingUrl={editingWhitelistUrl}
                                      setEditingId={setEditingWhitelistId}
                                      setEditingUrl={setEditingWhitelistUrl}
                                      onSave={handleUpdateWhitelist}
                                      onDelete={handleRemoveFromWhitelist}
                                    />
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </DragDropContext>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid item xs={12} xl={6}>
                        <Box sx={{ ...INSET_PANEL_SX, p: 2.25, height: '100%' }}>
                          <Typography variant="h6" sx={{ color: '#0B2E43', mb: 0.75 }}>
                            Blocklist
                          </Typography>
                          <form onSubmit={handleAddToBlacklist}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                              <TextField
                                value={newBlacklistUrl}
                                onChange={(event) => setNewBlacklistUrl(event.target.value)}
                                label="Add blocklist entry"
                                fullWidth
                              />
                              <Button type="submit" variant="contained" color="error">
                                Add
                              </Button>
                              <IconButton color="primary" onClick={() => blacklistFileInput.current?.click()}>
                                <AttachFileIcon />
                              </IconButton>
                            </Stack>
                            <input
                              ref={blacklistFileInput}
                              type="file"
                              accept=".csv,.txt"
                              onChange={handleBlacklistFileUpload}
                              style={{ display: 'none' }}
                            />
                          </form>

                          <Box sx={{ mt: 2 }}>
                            <DragDropContext onDragEnd={handleDragEnd}>
                              <Droppable droppableId="blacklist" type="blacklist">
                                {(provided) => (
                                  <div ref={provided.innerRef} {...provided.droppableProps}>
                                    <BlackList
                                      blacklist={blacklist}
                                      editingId={editingBlacklistId}
                                      editingUrl={editingBlacklistUrl}
                                      setEditingId={setEditingBlacklistId}
                                      setEditingUrl={setEditingBlacklistUrl}
                                      onSave={handleUpdateBlacklist}
                                      onDelete={handleRemoveFromBlacklist}
                                    />
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </DragDropContext>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
          )}

          {activeDashboardPage === 'organization' && (
        <Box id="organization" sx={SECTION_ANCHOR_SX}>
        <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
          <Card sx={PANEL_SX}>
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                  Organization
                </Typography>
                <form onSubmit={handleOrganizationRename}>
                  <Stack spacing={2}>
                    <TextField
                      label="Organization name"
                      value={organizationName}
                      onChange={(event) => setOrganizationName(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Approved email domain"
                      value={organizationDomain}
                      onChange={(event) => setOrganizationDomain(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      select
                      label="Institution type"
                      value={institutionType}
                      onChange={(event) => setInstitutionType(event.target.value)}
                      fullWidth
                    >
                      <MenuItem value="district">District office</MenuItem>
                      <MenuItem value="school">Single school</MenuItem>
                      <MenuItem value="charter_network">Charter network</MenuItem>
                      <MenuItem value="library">Library or media program</MenuItem>
                    </TextField>
                    <TextField
                      select
                      label="Deployment method"
                      value={deploymentMethod}
                      onChange={(event) => setDeploymentMethod(event.target.value)}
                      fullWidth
                    >
                      <MenuItem value="manual">Manual pilot rollout</MenuItem>
                      <MenuItem value="chrome_enterprise">Chrome Enterprise / Google Admin</MenuItem>
                      <MenuItem value="intune">Microsoft Intune</MenuItem>
                      <MenuItem value="jamf">Jamf School / Jamf Pro</MenuItem>
                    </TextField>
                    <TextField
                      label="Primary contact"
                      value={contactName}
                      onChange={(event) => setContactName(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Contact email"
                      value={contactEmail}
                      onChange={(event) => setContactEmail(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Operations notes"
                      value={organizationNotes}
                      onChange={(event) => setOrganizationNotes(event.target.value)}
                      multiline
                      minRows={3}
                      fullWidth
                    />
                    <TextField
                      select
                      label="Staff override policy"
                      value={staffOverride ? 'enabled' : 'disabled'}
                      onChange={(event) => setStaffOverride(event.target.value === 'enabled')}
                      fullWidth
                    >
                      <MenuItem value="disabled">Disabled by default</MenuItem>
                      <MenuItem value="enabled">Allow documented staff override</MenuItem>
                    </TextField>
                    <Button type="submit" variant="contained">
                      Save Organization Settings
                    </Button>
                  </Stack>
                </form>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleLeaveWorkspace}
                    disabled={organizationRole === 'org_owner'}
                  >
                    Leave Workspace
                  </Button>
                  <Button variant="text" startIcon={<FeedbackIcon />} onClick={() => setFeedbackOpen(true)}>
                    Send Product Feedback
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Card sx={PANEL_SX}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Typography variant="h4" sx={{ color: '#0B2E43', mb: 1 }}>
                  Staff access
                </Typography>
                <form onSubmit={handleAddMember}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <TextField
                      label="Existing user email"
                      value={memberEmail}
                      onChange={(event) => setMemberEmail(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      select
                      label="Role"
                      value={memberRole}
                      onChange={(event) => setMemberRole(event.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value="org_admin">{formatOrganizationRole('org_admin')}</MenuItem>
                      <MenuItem value="policy_admin">{formatOrganizationRole('policy_admin')}</MenuItem>
                      <MenuItem value="viewer">{formatOrganizationRole('viewer')}</MenuItem>
                    </TextField>
                    <Button type="submit" variant="contained">
                      Add Member
                    </Button>
                  </Stack>
                </form>

                <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                  {members.map((member) => (
                    <Box
                      key={member._id}
                      sx={{
                        ...INSET_PANEL_SX,
                        p: 2,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1.5,
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', md: 'center' },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '50%',
                            flexShrink: 0,
                            display: 'grid',
                            placeItems: 'center',
                            background: 'linear-gradient(140deg, rgba(14,77,110,0.12) 0%, rgba(19,148,127,0.16) 100%)',
                            color: '#0E4D6E',
                            fontWeight: 800,
                            fontSize: '0.9rem',
                            textTransform: 'uppercase',
                          }}
                        >
                          {(member.username || member.email || '?').charAt(0)}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography sx={{ color: '#0B2E43', fontWeight: 750 }}>{member.username}</Typography>
                            {member.organization_role === 'org_owner' && (
                              <Chip size="small" label="Owner" sx={{ bgcolor: 'rgba(223,162,58,0.16)', color: '#926212', fontWeight: 800 }} />
                            )}
                            {member._id === user?._id && (
                              <Chip size="small" label="You" sx={{ bgcolor: 'rgba(14,77,110,0.08)', color: '#0E4D6E', fontWeight: 800 }} />
                            )}
                          </Stack>
                          <Typography sx={{ color: '#52677A', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {member.email}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <TextField
                          select
                          size="small"
                          value={member.organization_role}
                          onChange={(event) => handleMemberRoleChange(member._id, event.target.value)}
                          sx={{ minWidth: 170 }}
                        >
                          <MenuItem value="org_owner">{formatOrganizationRole('org_owner')}</MenuItem>
                          <MenuItem value="org_admin">{formatOrganizationRole('org_admin')}</MenuItem>
                          <MenuItem value="policy_admin">{formatOrganizationRole('policy_admin')}</MenuItem>
                          <MenuItem value="viewer">{formatOrganizationRole('viewer')}</MenuItem>
                        </TextField>
                        {organizationRole === 'org_owner' && member._id !== user?._id && (
                          <Button size="small" onClick={() => handleTransferOwnership(member._id)}>
                            Make Owner
                          </Button>
                        )}
                        {member.organization_role !== 'org_owner' && (
                          <Button size="small" color="error" onClick={() => handleRemoveMember(member._id)} disabled={member._id === user?._id}>
                            Remove
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ color: '#0B2E43', mb: 0.75 }}>
                  Pending invitations
                </Typography>
                <Typography sx={{ color: '#52677A', lineHeight: 1.7, mb: 2 }}>
                  Invite staff who are not in the workspace yet.
                </Typography>
                <form onSubmit={handleCreateInvitation}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <TextField
                      label="Invite email"
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      select
                      label="Role"
                      value={inviteRole}
                      onChange={(event) => setInviteRole(event.target.value)}
                      sx={{ minWidth: 180 }}
                    >
                      <MenuItem value="org_admin">{formatOrganizationRole('org_admin')}</MenuItem>
                      <MenuItem value="policy_admin">{formatOrganizationRole('policy_admin')}</MenuItem>
                      <MenuItem value="viewer">{formatOrganizationRole('viewer')}</MenuItem>
                    </TextField>
                    <Button type="submit" variant="contained">
                      Invite
                    </Button>
                  </Stack>
                </form>

                <Stack spacing={1.5} sx={{ mt: 2.5 }}>
                  {invitations.length === 0 && (
                    <Typography sx={{ color: '#7A8FA0' }}>
                      No invitations sent yet.
                    </Typography>
                  )}
                  {invitations.map((invitation) => (
                    <Box
                      key={invitation.id}
                      sx={{
                        ...INSET_PANEL_SX,
                        p: 2,
                      }}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 750 }}>{invitation.email}</Typography>
                        <Chip
                          size="small"
                          label={formatOrganizationRole(invitation.role)}
                          sx={{ bgcolor: 'rgba(14,77,110,0.08)', color: '#0E4D6E', fontWeight: 700 }}
                        />
                        <Chip
                          size="small"
                          label={invitation.status}
                          sx={{
                            bgcolor: invitation.status === 'pending' ? 'rgba(223,162,58,0.16)' : 'rgba(19,148,127,0.12)',
                            color: invitation.status === 'pending' ? '#926212' : '#0C6E5E',
                            fontWeight: 800,
                            textTransform: 'capitalize',
                          }}
                        />
                      </Stack>
                      <Box
                        sx={{
                          mt: 1.25,
                          px: 1.5,
                          py: 0.9,
                          borderRadius: '8px',
                          bgcolor: '#ffffff',
                          border: '1px dashed #C3D3DD',
                          fontFamily: '"SFMono-Regular", "Menlo", monospace',
                          fontSize: '0.76rem',
                          color: '#52677A',
                          wordBreak: 'break-all',
                        }}
                      >
                        {invitation.invitation_link}
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                        <Button size="small" variant="outlined" onClick={() => handleCopyInvitationLink(invitation.invitation_link)}>
                          Copy Link
                        </Button>
                        {invitation.status === 'pending' && (
                          <Button size="small" onClick={() => handleResendInvitation(invitation)}>
                            Resend
                          </Button>
                        )}
                        {invitation.status === 'pending' && (
                          <Button size="small" color="error" onClick={() => handleRevokeInvitation(invitation.id)}>
                            Revoke
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
          )}

          {activeDashboardPage === 'devices' && (
          <Box id="devices" sx={SECTION_ANCHOR_SX}>
            <Card sx={PANEL_SX}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack spacing={2}>
                  <Box sx={{ ...INSET_PANEL_SX, p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                      <Box>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 750 }}>Recommended rollout</Typography>
                        <Typography sx={{ color: '#52677A', mt: 0.45, lineHeight: 1.55 }}>
                          Connect roster sync for real deployments. Use CSV for a quick import, or create a manual code for one test device.
                        </Typography>
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button variant="outlined" onClick={handleCopyRosteringChecklist}>
                          Copy IT Checklist
                        </Button>
                        <Button type="button" variant="text" onClick={handleCopyRosterTemplate}>
                          Copy CSV Template
                        </Button>
                        <Button type="button" variant="outlined" onClick={() => rosterFileInput.current?.click()}>
                          Import CSV
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>

                  <Accordion
                    key={managedDevices.length > 0 ? 'roster-sync-configured' : 'roster-sync-empty'}
                    defaultExpanded={managedDevices.length === 0}
                    sx={{ ...ACCORDION_SX, boxShadow: 'none' }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>Roster sync</Typography>
                        <Typography sx={{ color: '#52677A', mt: 0.35 }}>
                          Best for SIS-driven student, class, and teacher updates.
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={1.5}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              select
                              label="School system"
                              value={rosteringProvider}
                              onChange={(event) => {
                                const provider = event.target.value;
                                const preset = rosteringPresets.find((item) => item.id === provider);
                                setRosteringProvider(provider);
                                setRosteringSyncMode(preset?.sync_mode || 'oneroster_api');
                              }}
                              fullWidth
                            >
                              {(rosteringPresets.length ? rosteringPresets : [{ id: 'oneroster', name: 'Generic OneRoster', sync_mode: 'oneroster_api' }]).map((preset) => (
                                <MenuItem key={preset.id} value={preset.id}>
                                  {preset.name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Connection URL"
                              value={rosteringBaseUrl}
                              onChange={(event) => setRosteringBaseUrl(event.target.value)}
                              helperText="Root URL before /users, /classes, and /enrollments."
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              label="Credential type"
                              value={rosteringAuthType}
                              onChange={(event) => setRosteringAuthType(event.target.value)}
                              fullWidth
                            >
                              <MenuItem value="bearer">Bearer token</MenuItem>
                              <MenuItem value="oauth2_client_credentials">OAuth2 client credentials</MenuItem>
                              <MenuItem value="basic">Basic client credentials</MenuItem>
                              <MenuItem value="none">No auth</MenuItem>
                            </TextField>
                          </Grid>
                          {rosteringAuthType === 'bearer' && (
                            <Grid item xs={12} md={8}>
                              <TextField
                                label={rosteringIntegration?.has_access_token ? 'Access token saved' : 'Access token'}
                                value={rosteringAccessToken}
                                onChange={(event) => setRosteringAccessToken(event.target.value)}
                                type="password"
                                fullWidth
                              />
                            </Grid>
                          )}
                          {rosteringAuthType !== 'bearer' && rosteringAuthType !== 'none' && (
                            <>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label="Client ID"
                                  value={rosteringClientId}
                                  onChange={(event) => setRosteringClientId(event.target.value)}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label={rosteringIntegration?.has_client_secret ? 'Client secret saved' : 'Client secret'}
                                  value={rosteringClientSecret}
                                  onChange={(event) => setRosteringClientSecret(event.target.value)}
                                  type="password"
                                  fullWidth
                                />
                              </Grid>
                            </>
                          )}
                        </Grid>
                        {rosteringAuthType === 'oauth2_client_credentials' && (
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={8}>
                              <TextField
                                label="OAuth2 token URL"
                                value={rosteringTokenUrl}
                                onChange={(event) => setRosteringTokenUrl(event.target.value)}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                label="OAuth2 scope"
                                value={rosteringScope}
                                onChange={(event) => setRosteringScope(event.target.value)}
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        )}
                        <Accordion defaultExpanded={false} sx={{ ...ACCORDION_SX, boxShadow: 'none' }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box>
                              <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>Sync options</Typography>
                              <Typography sx={{ color: '#52677A', mt: 0.35 }}>
                                Defaults create class profiles and roster rows automatically.
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Grid container spacing={1.5}>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  label="Sync mode"
                                  value={rosteringSyncMode}
                                  onChange={(event) => setRosteringSyncMode(event.target.value)}
                                  fullWidth
                                >
                                  <MenuItem value="oneroster_api">OneRoster API</MenuItem>
                                  <MenuItem value="clever_api">Clever API</MenuItem>
                                  <MenuItem value="oneroster_csv">OneRoster CSV/manual import</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  label="Class profiles"
                                  value={rosteringAutoProfiles ? 'yes' : 'no'}
                                  onChange={(event) => setRosteringAutoProfiles(event.target.value === 'yes')}
                                  fullWidth
                                >
                                  <MenuItem value="yes">Create missing profiles</MenuItem>
                                  <MenuItem value="no">Roster only</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  label="Roster rows"
                                  value={rosteringAutoDevices ? 'yes' : 'no'}
                                  onChange={(event) => setRosteringAutoDevices(event.target.value === 'yes')}
                                  fullWidth
                                >
                                  <MenuItem value="yes">Create/update rows</MenuItem>
                                  <MenuItem value="no">Profiles only</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  label="Teachers"
                                  value={rosteringAutoTeachers ? 'yes' : 'no'}
                                  onChange={(event) => setRosteringAutoTeachers(event.target.value === 'yes')}
                                  fullWidth
                                >
                                  <MenuItem value="yes">Invite as limited staff</MenuItem>
                                  <MenuItem value="no">Do not invite</MenuItem>
                                </TextField>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  select
                                  label="Code expiry"
                                  value={rosteringDefaultCodeDays}
                                  onChange={(event) => setRosteringDefaultCodeDays(Number(event.target.value))}
                                  fullWidth
                                >
                                  <MenuItem value={7}>7 days</MenuItem>
                                  <MenuItem value={14}>14 days</MenuItem>
                                  <MenuItem value={30}>30 days</MenuItem>
                                </TextField>
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <Button variant="contained" onClick={handleSaveRosteringIntegration}>
                            Save Connection
                          </Button>
                          <Button variant="outlined" onClick={() => handleSyncRosteringIntegration(true)} disabled={rosteringSyncMode === 'oneroster_csv'}>
                            Dry Run
                          </Button>
                          <Button variant="outlined" onClick={() => handleSyncRosteringIntegration(false)} disabled={rosteringSyncMode === 'oneroster_csv'}>
                            Sync Now
                          </Button>
                        </Stack>
                        {rosteringSyncSummary && (
                          <Box sx={{ ...INSET_PANEL_SX, p: 2 }}>
                            <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                              Last sync
                            </Typography>
                            <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                              Received {rosteringSyncSummary.received_count || 0}, created {rosteringSyncSummary.created_count || 0}, updated {rosteringSyncSummary.updated_count || 0}, profile shells {rosteringSyncSummary.profile_created_count || 0}, teacher invites {rosteringSyncSummary.teacher_invited_count || 0}.
                            </Typography>
                            {(rosteringSyncSummary.teacher_existing_member_count || rosteringSyncSummary.teacher_existing_invitation_count) > 0 && (
                              <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                                Teachers already in workspace: {rosteringSyncSummary.teacher_existing_member_count || 0}; pending invites: {rosteringSyncSummary.teacher_existing_invitation_count || 0}.
                              </Typography>
                            )}
                            {Array.isArray(rosteringSyncSummary.skipped_rows) && rosteringSyncSummary.skipped_rows.length > 0 && (
                              <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                                Skipped rows: {rosteringSyncSummary.skipped_rows.slice(0, 5).map((row) => `${row.row} (${row.reason})`).join(', ')}
                              </Typography>
                            )}
                            {Array.isArray(rosteringSyncSummary.teacher_skipped_rows) && rosteringSyncSummary.teacher_skipped_rows.length > 0 && (
                              <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                                Skipped teachers: {rosteringSyncSummary.teacher_skipped_rows.slice(0, 5).map((row) => `${row.email || row.name || `row ${row.row}`} (${row.reason})`).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion defaultExpanded={false} sx={{ ...ACCORDION_SX, boxShadow: 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>Manual test code</Typography>
                        <Typography sx={{ color: '#52677A', mt: 0.35 }}>
                          Create one enrollment code for a pilot device, room, or shared cart.
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <form onSubmit={handleCreateManagedDevice}>
                        <Stack spacing={1.5}>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Device label"
                                value={deviceLabel}
                                onChange={(event) => setDeviceLabel(event.target.value)}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Student or room"
                                value={deviceStudentName}
                                onChange={(event) => setDeviceStudentName(event.target.value)}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <TextField
                                select
                                label="Default policy"
                                value={deviceAssignedProfileId}
                                InputLabelProps={{ shrink: true }}
                                SelectProps={{
                                  displayEmpty: true,
                                  renderValue: (value) =>
                                    value
                                      ? `District baseline + ${profiles.find((profile) => getItemId(profile) === value)?.profile_name || 'Policy profile'}`
                                      : 'District baseline only',
                                }}
                                onChange={(event) => setDeviceAssignedProfileId(event.target.value)}
                                helperText="District baseline always applies."
                                fullWidth
                              >
                                <MenuItem value="">District baseline only</MenuItem>
                                {profiles.map((profile) => (
                                  <MenuItem key={getItemId(profile)} value={getItemId(profile)}>
                                    {profile.profile_name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                select
                                label="Code expires"
                                value={deviceExpiresInDays}
                                onChange={(event) => setDeviceExpiresInDays(Number(event.target.value))}
                                fullWidth
                              >
                                <MenuItem value={3}>3 days</MenuItem>
                                <MenuItem value={7}>7 days</MenuItem>
                                <MenuItem value={14}>14 days</MenuItem>
                                <MenuItem value={30}>30 days</MenuItem>
                              </TextField>
                            </Grid>
                          </Grid>
                          <Accordion defaultExpanded={false} sx={{ ...ACCORDION_SX, boxShadow: 'none' }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box>
                                <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>Student details</Typography>
                                <Typography sx={{ color: '#52677A', mt: 0.35 }}>
                                  Optional one-off fields for schedule matching.
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              <Stack spacing={1.5}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                                  <TextField
                                    label="Student email"
                                    value={deviceStudentEmail}
                                    onChange={(event) => setDeviceStudentEmail(event.target.value)}
                                    fullWidth
                                  />
                                  <TextField
                                    label="Student ID"
                                    value={deviceStudentId}
                                    onChange={(event) => setDeviceStudentId(event.target.value)}
                                    fullWidth
                                  />
                                </Stack>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                                  <TextField
                                    label="Primary class/group"
                                    value={deviceGroupName}
                                    onChange={(event) => setDeviceGroupName(event.target.value)}
                                    helperText="Optional fallback, such as Film 2A."
                                    fullWidth
                                  />
                                  <TextField
                                    label="Primary teacher"
                                    value={deviceTeacherName}
                                    onChange={(event) => setDeviceTeacherName(event.target.value)}
                                    helperText="Optional fallback teacher."
                                    fullWidth
                                  />
                                </Stack>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                                  <TextField
                                    label="All classes/sections"
                                    value={deviceClassNames}
                                    onChange={(event) => setDeviceClassNames(event.target.value)}
                                    helperText="Comma-separated, e.g. Film 2A, English 3, Biology."
                                    fullWidth
                                  />
                                  <TextField
                                    label="All teachers"
                                    value={deviceTeacherNames}
                                    onChange={(event) => setDeviceTeacherNames(event.target.value)}
                                    helperText="Comma-separated names for schedule matching."
                                    fullWidth
                                  />
                                </Stack>
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <Button type="submit" variant="contained">
                              Create Code
                            </Button>
                            <Button type="button" variant="text" onClick={handleCopyRosterTemplate}>
                              Copy CSV Template
                            </Button>
                          </Stack>
                        </Stack>
                      </form>
                    </AccordionDetails>
                  </Accordion>

                  {managedDeviceMessage && (
                    <Alert severity={managedDeviceMessage.severity}>
                      {managedDeviceMessage.text}
                    </Alert>
                  )}
                  <input
                    ref={rosterFileInput}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={handleRosterFileUpload}
                  />
                  {rosterImportSummary && (
                    <Box sx={{ ...INSET_PANEL_SX, p: 2 }}>
                      <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                        Last roster import
                      </Typography>
                      <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                        Created {rosterImportSummary.created_count}, updated {rosterImportSummary.updated_count}, skipped {rosterImportSummary.skipped_rows.length}.
                      </Typography>
                      {rosterImportSummary.skipped_rows.length > 0 && (
                        <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                          Skipped rows: {rosterImportSummary.skipped_rows.map((row) => `${row.row} (${row.reason})`).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Accordion
                    key={managedDevices.length > 0 ? 'managed-roster-with-records' : 'managed-roster-empty'}
                    defaultExpanded={managedDevices.length > 0}
                    sx={{ ...ACCORDION_SX, boxShadow: 'none' }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>Enrolled students and devices</Typography>
                        <Typography sx={{ color: '#52677A', mt: 0.35 }}>
                          {managedDevices.length > 0
                            ? `${managedDevices.length} record${managedDevices.length === 1 ? '' : 's'} ready to review or assign.`
                            : 'Records appear here after roster sync, CSV import, or manual enrollment.'}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      {managedDevices.length === 0 ? (
                        <Box sx={{ ...INSET_PANEL_SX, p: 2 }}>
                          <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                            No devices enrolled yet
                          </Typography>
                          <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                            Start with roster sync, import a CSV, or create one manual test code.
                          </Typography>
                        </Box>
                      ) : (
                        <Stack spacing={2}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}>
                            <Chip
                              label={`${selectedDeviceIds.length} selected`}
                              sx={{ bgcolor: selectedDeviceIds.length ? 'rgba(19,148,127,0.14)' : 'rgba(11,46,67,0.08)', color: '#456E80', fontWeight: 800 }}
                            />
                            <TextField
                              select
                              label="Status"
                              value={deviceStatusFilter}
                              onChange={(event) => setDeviceStatusFilter(event.target.value)}
                              sx={{ minWidth: 180 }}
                            >
                              <MenuItem value="all">All devices</MenuItem>
                              <MenuItem value="active">Active</MenuItem>
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="revoked">Revoked</MenuItem>
                              <MenuItem value="expired">Expired</MenuItem>
                            </TextField>
                            <TextField
                              select
                              label="Assign selected to"
                              value={bulkDeviceProfileId}
                              onChange={(event) => setBulkDeviceProfileId(event.target.value)}
                              sx={{ minWidth: 220 }}
                            >
                              <MenuItem value="">District baseline only</MenuItem>
                              {profiles.map((profile) => (
                                <MenuItem key={getItemId(profile)} value={getItemId(profile)}>
                                  {profile.profile_name}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Stack>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <Button variant="outlined" onClick={() => handleBulkDeviceAction('assign_profile')}>
                              Apply Profile
                            </Button>
                            <Button variant="outlined" onClick={() => handleBulkDeviceAction('clear_profile')}>
                              Baseline Only
                            </Button>
                            <Button variant="text" onClick={toggleVisibleDeviceSelection}>
                              {allVisibleDevicesSelected ? 'Unselect Visible' : 'Select Visible'}
                            </Button>
                            <Button variant="text" onClick={clearDeviceSelection}>
                              Clear Selection
                            </Button>
                            <Button color="error" variant="outlined" onClick={() => handleBulkDeviceAction('revoke')}>
                              Revoke Selected
                            </Button>
                          </Stack>

                          <Stack spacing={1.5}>
                            {filteredManagedDevices.length === 0 && (
                              <Typography sx={{ color: '#7A8FA0' }}>
                                No managed devices match this filter yet.
                              </Typography>
                            )}
                            {filteredManagedDevices.map((device) => {
                              const tokenTiming = getDeviceTokenTiming(device.auth_token_expires_at);
                              const statusStyle =
                                device.status === 'active'
                                  ? { bgcolor: 'rgba(19,148,127,0.12)', color: '#0C6E5E' }
                                  : device.status === 'pending'
                                    ? { bgcolor: 'rgba(223,162,58,0.16)', color: '#926212' }
                                    : { bgcolor: 'rgba(192,69,49,0.1)', color: '#9A3322' };
                              const metaRows = [
                                {
                                  label: 'Policy',
                                  value: device.assigned_profile_name
                                    ? `District baseline + ${device.assigned_profile_name}`
                                    : 'District baseline only',
                                },
                                (device.group_name || device.teacher_name) && {
                                  label: 'Group',
                                  value: `${device.group_name || 'No group'} • ${device.teacher_name || 'No teacher'}`,
                                },
                                Array.isArray(device.class_names) && device.class_names.length > 0 && {
                                  label: 'Classes',
                                  value: device.class_names.join(', '),
                                },
                                Array.isArray(device.teacher_names) && device.teacher_names.length > 0 && {
                                  label: 'Teachers',
                                  value: device.teacher_names.join(', '),
                                },
                                (device.student_email || device.student_id) && {
                                  label: 'Student',
                                  value: `${device.student_email || 'No email'} • ${device.student_id || 'No student ID'}`,
                                },
                                device.device_name && {
                                  label: 'Device',
                                  value: device.device_name,
                                },
                                {
                                  label: 'Last seen',
                                  value: device.last_seen_at ? new Date(device.last_seen_at).toLocaleString() : 'Not enrolled yet',
                                },
                              ].filter(Boolean);
                              return (
                                <Box
                                  key={device.id}
                                  sx={{
                                    ...INSET_PANEL_SX,
                                    p: 2,
                                    backgroundColor: '#ffffff',
                                    transition: 'border-color 140ms ease, box-shadow 140ms ease',
                                    '&:hover': {
                                      borderColor: 'rgba(19,148,127,0.35)',
                                      boxShadow: '0 8px 20px -12px rgba(11,46,67,0.18)',
                                    },
                                  }}
                                >
                                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                                    <Stack direction="row" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                                      <Checkbox
                                        checked={selectedDeviceIds.includes(device.id)}
                                        onChange={() => toggleDeviceSelection(device.id)}
                                        sx={{ alignSelf: 'flex-start', mt: -0.75, ml: -0.75 }}
                                      />
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                                          <Typography sx={{ color: '#0B2E43', fontWeight: 750 }}>{device.label}</Typography>
                                          <Chip size="small" label={device.status} sx={{ ...statusStyle, fontWeight: 800, textTransform: 'capitalize' }} />
                                          <Typography sx={{ color: '#7A8FA0', fontSize: '0.85rem' }}>
                                            {device.student_name || 'Shared device'}
                                          </Typography>
                                        </Stack>
                                        <Box
                                          sx={{
                                            mt: 1.25,
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                            columnGap: 2.5,
                                            rowGap: 0.75,
                                          }}
                                        >
                                          {metaRows.map((row) => (
                                            <Stack key={row.label} direction="row" spacing={1} sx={{ minWidth: 0 }}>
                                              <Typography
                                                sx={{
                                                  color: '#8CA3B2',
                                                  fontSize: '0.78rem',
                                                  fontWeight: 700,
                                                  flexShrink: 0,
                                                  minWidth: 64,
                                                }}
                                              >
                                                {row.label}
                                              </Typography>
                                              <Typography sx={{ color: '#3E5A6C', fontSize: '0.83rem', wordBreak: 'break-word' }}>
                                                {row.value}
                                              </Typography>
                                            </Stack>
                                          ))}
                                        </Box>
                                        {tokenTiming?.isExpired && (
                                          <Alert severity="error" sx={{ mt: 1.25, py: 0.25 }}>
                                            Device auth needs attention — it stopped refreshing automatically on{' '}
                                            {new Date(device.auth_token_expires_at).toLocaleString()}
                                            {device.last_token_refreshed_at
                                              ? ` (last refreshed ${new Date(device.last_token_refreshed_at).toLocaleString()})`
                                              : ''}
                                            .
                                          </Alert>
                                        )}
                                        {device.status === 'pending' && device.enrollment_code && (
                                          <Box
                                            sx={{
                                              mt: 1.25,
                                              px: 1.5,
                                              py: 0.9,
                                              borderRadius: '8px',
                                              bgcolor: '#F4F8FA',
                                              border: '1px dashed #C3D3DD',
                                              fontFamily: '"SFMono-Regular", "Menlo", monospace',
                                              fontSize: '0.8rem',
                                              color: '#0E4D6E',
                                              wordBreak: 'break-all',
                                            }}
                                          >
                                            {device.enrollment_code}
                                          </Box>
                                        )}
                                      </Box>
                                    </Stack>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignSelf: { md: 'flex-start' }, flexShrink: 0 }}>
                                      <Button size="small" variant="outlined" onClick={() => handleOpenManagedDeviceEditor(device)}>
                                        Edit
                                      </Button>
                                      <Button size="small" onClick={() => handleRotateManagedDevice(device.id)}>
                                        Rotate Token
                                      </Button>
                                      {device.status === 'pending' && device.enrollment_code && (
                                        <Button
                                          size="small"
                                          onClick={() =>
                                            copyToClipboard(
                                              device.enrollment_code,
                                              () => showManagedDeviceMessage('success', `Enrollment code copied: ${device.enrollment_code}`),
                                              () => showManagedDeviceMessage('error', 'Unable to copy the enrollment code on this browser.')
                                            )
                                          }
                                        >
                                          Copy Code
                                        </Button>
                                      )}
                                      {device.status !== 'revoked' && (
                                        <Button size="small" color="error" onClick={() => handleRevokeManagedDevice(device.id)}>
                                          Revoke
                                        </Button>
                                      )}
                                    </Stack>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Stack>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </CardContent>
            </Card>
          </Box>
          )}

          {activeDashboardPage === 'reports' && (
        <Box id="reports" sx={SECTION_ANCHOR_SX}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={PANEL_SX}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="flex-end" spacing={2} sx={{ mb: 2.5 }}>
                  <TextField
                    select
                    label="Window"
                    value={String(reportDays)}
                    onChange={(event) => setReportDays(Number(event.target.value))}
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="7">7 days</MenuItem>
                    <MenuItem value="30">30 days</MenuItem>
                    <MenuItem value="90">90 days</MenuItem>
                  </TextField>
                </Stack>

                <Grid container spacing={2}>
                  {[
                    { label: 'Scans', value: reportOverview.total, accent: '#0E4D6E', tint: 'rgba(14,77,110,0.1)', icon: <ManageSearchOutlinedIcon fontSize="small" /> },
                    { label: 'Allowed', value: reportOverview.allowed, accent: '#13947F', tint: 'rgba(19,148,127,0.1)', icon: <TaskAltOutlinedIcon fontSize="small" /> },
                    { label: 'Blocked', value: reportOverview.blocked, accent: '#C04531', tint: 'rgba(192,69,49,0.09)', icon: <BlockOutlinedIcon fontSize="small" /> },
                    { label: 'Block rate', value: `${reportOverview.blockRate}%`, accent: '#7A4E16', tint: 'rgba(122,78,22,0.1)', icon: <AssessmentIcon fontSize="small" /> },
                  ].map((stat) => (
                    <Grid item xs={12} sm={6} md={3} key={stat.label}>
                      <Box
                        sx={{
                          borderRadius: INNER_RADIUS,
                          border: '1px solid #E6EDF2',
                          backgroundColor: '#ffffff',
                          p: 2.25,
                          height: '100%',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Box>
                            <Typography sx={STAT_LABEL_SX}>{stat.label}</Typography>
                            <Typography sx={{ ...STAT_VALUE_SX, color: stat.accent }}>
                              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: '10px',
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: stat.tint,
                              color: stat.accent,
                              flexShrink: 0,
                            }}
                          >
                            {stat.icon}
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {!hasReportActivity ? (
                  <Box sx={{ ...INSET_PANEL_SX, mt: 2.5, p: 2.5 }}>
                    <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                      No activity yet
                    </Typography>
                    <Typography sx={{ color: '#52677A', mt: 0.75 }}>
                      Once enrolled devices start scanning, this section will show recent decisions, top domains, and exportable reports.
                    </Typography>
                    <Button variant="outlined" size="small" sx={{ mt: 1.75 }} onClick={() => handleDashboardPageChange('devices')}>
                      Enroll a Device
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ ...INSET_PANEL_SX, mt: 2.5, p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1.25 }}>
                        <Typography sx={{ color: '#13293A', fontWeight: 750, fontSize: '0.94rem' }}>Decision mix</Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#13947F' }} />
                            <Typography sx={{ color: '#52677A', fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
                              {reportOverview.allowedPct}% allowed
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#C04531' }} />
                            <Typography sx={{ color: '#52677A', fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
                              {reportOverview.blockedPct}% blocked
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                      <Box sx={{ display: 'flex', height: 12, borderRadius: '999px', overflow: 'hidden', bgcolor: 'rgba(11,46,67,0.07)' }}>
                        <Box sx={{ width: `${reportOverview.allowedPct}%`, background: 'linear-gradient(90deg, #13947F, #16A38C)', transition: 'width 400ms ease' }} />
                        <Box sx={{ width: `${reportOverview.blockedPct}%`, background: 'linear-gradient(90deg, #D05A47, #C04531)', transition: 'width 400ms ease' }} />
                      </Box>
                      <Grid container spacing={1.25} sx={{ mt: 1.75 }}>
                        {[
                          {
                            label: 'Most active domain',
                            value: reportOverview.topDomain?.domain || 'None yet',
                            helper: reportOverview.topDomain ? `${reportOverview.topDomain.count} scans` : 'No domain activity in this window',
                          },
                          {
                            label: 'Most blocked domain',
                            value: reportOverview.topBlockedDomain?.domain || 'None yet',
                            helper: reportOverview.topBlockedDomain ? `${reportOverview.topBlockedDomain.count} blocked` : 'No blocked domains in this window',
                          },
                          {
                            label: 'Domains to review',
                            value: reportOverview.mixedDomains.length.toLocaleString(),
                            helper: 'Allowed and blocked activity on the same domain',
                          },
                          {
                            label: 'Decision path',
                            value: reportOverview.sources.length === 1 ? reportOverview.sources[0].label : `${reportOverview.sources.length} sources`,
                            helper: reportOverview.sources.length === 1 ? 'All activity used one decision path' : reportOverview.sources.map((source) => source.label).join(', '),
                          },
                        ].map((insight) => (
                          <Grid item xs={12} md={3} key={insight.label}>
                            <Box sx={{ borderRadius: INNER_RADIUS, bgcolor: '#FFFFFF', border: '1px solid #E6EDF2', p: 1.5, height: '100%' }}>
                              <Typography sx={STAT_LABEL_SX}>{insight.label}</Typography>
                              <Typography sx={{ color: '#0B2E43', fontWeight: 800, mt: 0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {insight.value}
                              </Typography>
                              <Typography sx={{ color: '#52677A', fontSize: '0.82rem', mt: 0.35 }}>
                                {insight.helper}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2.5, mb: 2.5 }}>
                      <Button variant="contained" onClick={handleExportReport}>
                        Export Activity CSV
                      </Button>
                      <Button variant="outlined" onClick={handleExportAudit}>
                        Export Audit CSV
                      </Button>
                      <Button variant="outlined" onClick={handleExportSupportBundle}>
                        Export Support Bundle
                      </Button>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mb: 2.5 }}>
                      <TextField
                        label="Search events"
                        value={reportQuery}
                        onChange={(event) => setReportQuery(event.target.value)}
                        fullWidth
                      />
                      <TextField
                        select
                        label="Decision"
                        value={reportDecisionFilter}
                        onChange={(event) => setReportDecisionFilter(event.target.value)}
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="allow">Allow</MenuItem>
                        <MenuItem value="block">Block</MenuItem>
                      </TextField>
                      <TextField
                        select
                        label="Source"
                        value={reportSourceFilter}
                        onChange={(event) => setReportSourceFilter(event.target.value)}
                        sx={{ minWidth: 180 }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="ai_policy">AI policy</MenuItem>
                        <MenuItem value="allowlist">Allowlist</MenuItem>
                        <MenuItem value="blocklist">Blocklist</MenuItem>
                        <MenuItem value="default_allow">Default allow</MenuItem>
                        <MenuItem value="low_signal_allow">Low signal allow</MenuItem>
                        <MenuItem value="temporary_override">Temporary allow</MenuItem>
                      </TextField>
                    </Stack>

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                      {[
                        {
                          title: 'Top domains',
                          empty: 'No domain activity in this window yet.',
                          accent: '#0E4D6E',
                          rows: (reportSummary?.top_domains || []).slice(0, 5).map((domain) => ({ label: domain.domain, count: domain.count })),
                        },
                        {
                          title: 'Domains needing review',
                          empty: 'No domains have both allowed and blocked activity in this window.',
                          accent: '#7A4E16',
                          rows: (reportSummary?.mixed_decision_domains || []).slice(0, 5).map((domain) => ({
                            label: domain.domain,
                            count: domain.count,
                            helper: `${domain.blocked} blocked / ${domain.allowed} allowed`,
                          })),
                        },
                        {
                          title: 'Most blocked domains',
                          empty: 'No blocked domains in this window yet.',
                          accent: '#C04531',
                          rows: (reportSummary?.blocked_domains || []).slice(0, 5).map((domain) => ({ label: domain.domain, count: domain.count })),
                        },
                        {
                          title: 'Busiest students/devices',
                          empty: 'No student or device activity in this window yet.',
                          accent: '#13947F',
                          rows: (reportSummary?.actor_activity || []).slice(0, 5).map((actor) => ({ label: actor.actor, count: actor.count })),
                        },
                      ].map((block) => {
                        const maxCount = Math.max(...block.rows.map((row) => row.count || 0), 1);
                        return (
                          <Grid item xs={12} md={6} key={block.title}>
                            <Box sx={{ ...INSET_PANEL_SX, p: 2, height: '100%' }}>
                              <Typography sx={{ color: '#13293A', fontWeight: 750, mb: 1.5, fontSize: '0.94rem' }}>
                                {block.title}
                              </Typography>
                              <Stack spacing={1.25}>
                                {block.rows.length === 0 && (
                                  <Typography sx={{ color: '#7A8FA0', fontSize: '0.88rem' }}>
                                    {block.empty}
                                  </Typography>
                                )}
                                {block.rows.map((row) => (
                                  <Box key={row.label}>
                                    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.5 }}>
                                      <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                          sx={{
                                            color: '#3E5A6C',
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {row.label}
                                        </Typography>
                                        {row.helper && (
                                          <Typography sx={{ color: '#7A8FA0', fontSize: '0.78rem', mt: 0.15 }}>
                                            {row.helper}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Typography sx={{ color: '#0B2E43', fontSize: '0.85rem', fontWeight: 750, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                        {row.count}
                                      </Typography>
                                    </Stack>
                                    <Box sx={{ height: 6, borderRadius: '999px', bgcolor: 'rgba(11,46,67,0.07)', overflow: 'hidden' }}>
                                      <Box
                                        sx={{
                                          height: '100%',
                                          width: `${Math.max(((row.count || 0) / maxCount) * 100, 4)}%`,
                                          borderRadius: '999px',
                                          background: `linear-gradient(90deg, ${block.accent}, ${block.accent}CC)`,
                                          transition: 'width 400ms ease',
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>

                    <Box sx={{ mt: 2.5 }}>
                      <Typography sx={{ color: '#0B2E43', fontWeight: 700, mb: 1 }}>Recent decisions</Typography>
                      <Stack spacing={1.25}>
                        {reportEvents.length === 0 && (
                          <Typography sx={{ color: '#52677A' }}>
                            No matching events in this window.
                          </Typography>
                        )}
                        {reportEvents.slice(0, 8).map((event) => {
                          const isBlock = String(event.decision).toLowerCase() === 'block';
                          const actorLabel = getReportActorLabel(event);
                          const actorDetails = getReportActorDetails(event, actorLabel);
                          const pageLabel = formatReportUrl(event);
                          const sourceLabel = formatReportSource(event.source);
                          return (
                            <Box
                              key={event.id}
                              sx={{
                                ...INSET_PANEL_SX,
                                p: 2,
                                borderLeft: `3px solid ${isBlock ? '#C04531' : '#13947F'}`,
                              }}
                            >
                              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                  size="small"
                                  label={isBlock ? 'Blocked' : 'Allowed'}
                                  sx={{
                                    bgcolor: isBlock ? 'rgba(192,69,49,0.12)' : 'rgba(19,148,127,0.12)',
                                    color: isBlock ? '#9A3322' : '#0C6E5E',
                                    fontWeight: 800,
                                  }}
                                />
                                <Typography
                                  title={event.current_url || pageLabel}
                                  sx={{
                                    color: '#0B2E43',
                                    fontWeight: 700,
                                    wordBreak: 'break-word',
                                    minWidth: 0,
                                  }}
                                >
                                  {pageLabel}
                                </Typography>
                              </Stack>
                              {event.domain && event.domain !== pageLabel && (
                                <Typography sx={{ color: '#52677A', mt: 0.55, fontSize: '0.86rem' }}>
                                  {event.domain}
                                </Typography>
                              )}
                              <Typography sx={{ color: '#52677A', mt: 0.4, fontSize: '0.86rem' }}>
                                {sourceLabel} • {event.profile_name || 'District baseline'} • {new Date(event.created_at).toLocaleString()}
                              </Typography>
                              {actorLabel && (
                                <Typography sx={{ color: '#52677A', mt: 0.5, fontSize: '0.86rem' }}>
                                  {actorLabel}{actorDetails.length ? ` • ${actorDetails.join(' • ')}` : ''}
                                </Typography>
                              )}
                              {event.reason && (
                                <Typography sx={{ color: '#52677A', mt: 0.5, fontSize: '0.86rem', fontStyle: 'italic' }}>
                                  “{event.reason}”
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
          )}

          {activeDashboardPage === 'operations' && (
        <Box id="operations" sx={SECTION_ANCHOR_SX}>
          <Stack spacing={2.5}>
            <Card sx={{ ...PANEL_SX, height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    sx={{
                      mb: 2.5,
                      p: 2,
                      borderRadius: INNER_RADIUS,
                      border: '1px solid',
                      borderColor:
                        opsSummary?.status === 'healthy'
                          ? 'rgba(19,148,127,0.3)'
                          : opsSummary?.status === 'critical'
                            ? 'rgba(192,69,49,0.3)'
                            : 'rgba(223,162,58,0.35)',
                      backgroundColor:
                        opsSummary?.status === 'healthy'
                          ? 'rgba(19,148,127,0.07)'
                          : opsSummary?.status === 'critical'
                            ? 'rgba(192,69,49,0.07)'
                            : 'rgba(223,162,58,0.1)',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor:
                            opsSummary?.status === 'healthy'
                              ? '#13947F'
                              : opsSummary?.status === 'critical'
                                ? '#C04531'
                                : '#DFA23A',
                          animation: 'softPulse 2.4s ease-in-out infinite',
                        }}
                      />
                      <Box>
                        <Typography sx={STAT_LABEL_SX}>Workspace health</Typography>
                        <Typography variant="h5" sx={{ color: '#0B2E43', mt: 0.25, textTransform: 'capitalize' }}>
                          {opsSummary?.status || 'Checking'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <Button variant="outlined" onClick={refreshOrganizationData}>
                        Refresh Checks
                      </Button>
                      <Button variant="outlined" onClick={handleSendSmtpTest}>
                        Send SMTP Test
                      </Button>
                      <Button variant="text" onClick={handleCopyOpsSnapshot}>
                        Copy Ops Snapshot
                      </Button>
                    </Stack>
                  </Stack>

                  <Grid container spacing={2}>
                    {[
                      { label: 'Active devices', value: opsSummary?.counts?.managed_devices_active ?? 0 },
                      { label: 'Pending enrollments', value: opsSummary?.counts?.managed_devices_pending ?? 0 },
                      { label: 'Stale devices', value: opsSummary?.counts?.managed_devices_stale ?? 0 },
                      { label: 'Pending staff invites', value: opsSummary?.counts?.pending_invitations ?? 0 },
                    ].map((stat) => (
                      <Grid item xs={12} sm={6} md={3} key={stat.label}>
                        <Box sx={{ ...INSET_PANEL_SX, p: 2 }}>
                          <Typography sx={STAT_LABEL_SX}>{stat.label}</Typography>
                          <Typography sx={STAT_VALUE_SX}>{stat.value}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ ...INSET_PANEL_SX, p: 2, height: '100%' }}>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 700, mb: 1.25 }}>Configuration checks</Typography>
                        <Stack spacing={1}>
                          <Typography sx={{ color: '#52677A' }}>
                            Database: {opsSummary?.database?.ok ? 'Connected' : 'Problem detected'}
                          </Typography>
                          <Typography sx={{ color: '#52677A' }}>
                            SMTP: {opsSummary?.configuration?.smtp_configured ? 'Configured' : 'Not configured'}
                          </Typography>
                          {opsSummary?.configuration?.smtp_host && (
                            <Typography sx={{ color: '#52677A' }}>
                              SMTP host: {opsSummary.configuration.smtp_host}
                            </Typography>
                          )}
                          <Typography sx={{ color: '#52677A' }}>
                            Auto-enrollment: {opsSummary?.configuration?.managed_auto_enrollment_supported ? 'Supported' : 'Unavailable'}
                          </Typography>
                          <Typography sx={{ color: '#52677A' }}>
                            AI providers: {Object.values(opsSummary?.configuration?.ai_providers || {}).filter(Boolean).length} connected
                          </Typography>
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ ...INSET_PANEL_SX, p: 2, height: '100%' }}>
                        <Typography sx={{ color: '#0B2E43', fontWeight: 700, mb: 1.25 }}>Warnings</Typography>
                        <Stack spacing={1}>
                          {(opsSummary?.warnings || []).length === 0 && (
                            <Typography sx={{ color: '#52677A' }}>
                              No operational warnings are currently flagged.
                            </Typography>
                          )}
                          {(opsSummary?.warnings || []).map((warning) => (
                            <Typography key={warning} sx={{ color: '#52677A' }}>
                              {warning}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ ...INSET_PANEL_SX, p: 2, mt: 2 }}>
                    <Typography sx={{ color: '#0B2E43', fontWeight: 700, mb: 1.25 }}>Recommended next steps</Typography>
                    <Stack spacing={1.25}>
                      {(opsSummary?.next_steps || []).map((step) => (
                        <Box key={`${step.priority}-${step.title}`}>
                          <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                            {step.title}
                          </Typography>
                          <Typography sx={{ color: '#52677A', mt: 0.35 }}>
                            {step.detail}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mt: 2.5 }}>
                    <Button variant="outlined" onClick={() => handleOrganizationCleanup('revoke_stale_devices')}>
                      Revoke Stale Devices
                    </Button>
                    <Button variant="outlined" onClick={() => handleOrganizationCleanup('expire_pending_device_codes')}>
                      Expire Old Pending Codes
                    </Button>
                    {user?.is_admin && (
                      <Button color="warning" variant="outlined" onClick={handleNormalizeDuplicateEmails}>
                        Normalize Duplicate User Emails
                      </Button>
                    )}
                  </Stack>

                  {(opsSummary?.duplicate_email_conflicts || []).length > 0 && (
                    <Box sx={{ ...INSET_PANEL_SX, p: 2, mt: 2 }}>
                      <Typography sx={{ color: '#0B2E43', fontWeight: 700, mb: 1 }}>
                        Duplicate user emails
                      </Typography>
                      <Stack spacing={1}>
                        {opsSummary.duplicate_email_conflicts.map((conflict) => (
                          <Box key={conflict.email} sx={{ display: 'flex', justifyContent: 'space-between', color: '#52677A' }}>
                            <Typography>{conflict.email}</Typography>
                            <Typography>{conflict.count}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>

            <Accordion defaultExpanded={false} sx={ACCORDION_SX}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography variant="h5" sx={{ color: '#0B2E43' }}>
                    Audit log
                  </Typography>
                  <Typography sx={{ color: '#52677A', mt: 0.4 }}>
                    Recent policy and workspace changes.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: { xs: 2, md: 2.5 }, pt: 0 }}>
              <Card sx={{ ...PANEL_SX, height: '100%' }}>
                <CardContent sx={{ p: 0 }}>
                  <Stack spacing={1.5}>
                    {auditLogs.length === 0 && (
                      <Typography sx={{ color: '#7A8FA0' }}>
                        No audit events yet.
                      </Typography>
                    )}
                    {auditLogs.map((log) => (
                      <Box
                        key={log.id}
                        sx={{
                          ...INSET_PANEL_SX,
                          p: 2,
                        }}
                      >
                        <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                          {log.action}
                        </Typography>
                        <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                          {log.actor_username} • {new Date(log.created_at).toLocaleString()}
                        </Typography>
                        {log.resource_type && (
                          <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                            {log.resource_type}{log.resource_id ? ` • ${log.resource_id}` : ''}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>

                  <Box sx={{ mt: 2.5 }}>
                    <Typography sx={{ color: '#0B2E43', fontWeight: 700, mb: 1 }}>Recent operational actions</Typography>
                    <Stack spacing={1.25}>
                      {(opsSummary?.recent_actions || []).length === 0 && (
                        <Typography sx={{ color: '#52677A' }}>
                          No recent operational activity yet.
                        </Typography>
                      )}
                      {(opsSummary?.recent_actions || []).map((action, index) => (
                        <Box key={`${action.action}-${action.created_at || index}`} sx={{ ...INSET_PANEL_SX, p: 2 }}>
                          <Typography sx={{ color: '#0B2E43', fontWeight: 700 }}>
                            {action.action}
                          </Typography>
                          <Typography sx={{ color: '#52677A', mt: 0.5 }}>
                            {action.actor_username || 'Unknown actor'} • {action.created_at ? new Date(action.created_at).toLocaleString() : 'Unknown time'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Box>
          )}
          </Stack>
      </Stack>
      </Box>

      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)}>
        <DialogTitle>Create new custom profile</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#52677A', mb: 1.5 }}>
            This adds a separate policy layer. District baseline stays as the default.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="New profile name"
            value={profileName}
            onChange={(event) => setProfileName(event.target.value)}
            helperText="Examples: Library Research, Testing Mode, Elementary Labs"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateProfile} variant="contained">
            Create New Profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)}>
        <DialogTitle>Clone policy profile</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="New profile name"
            value={cloneProfileName}
            onChange={(event) => setCloneProfileName(event.target.value)}
            helperText="The selected profile's prompts and lists will be copied into this new profile."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCloneProfile} variant="contained">
            Clone
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingManagedDevice)} onClose={handleCloseManagedDeviceEditor}>
        <DialogTitle>Edit managed device</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1, minWidth: { sm: 420 } }}>
            <TextField
              autoFocus
              label="Device label"
              value={editingDeviceLabel}
              onChange={(event) => setEditingDeviceLabel(event.target.value)}
              fullWidth
            />
            <TextField
              label="Student or room"
              value={editingDeviceStudentName}
              onChange={(event) => setEditingDeviceStudentName(event.target.value)}
              fullWidth
            />
            <TextField
              label="Student email"
              value={editingDeviceStudentEmail}
              onChange={(event) => setEditingDeviceStudentEmail(event.target.value)}
              fullWidth
            />
            <TextField
              label="Student ID"
              value={editingDeviceStudentId}
              onChange={(event) => setEditingDeviceStudentId(event.target.value)}
              fullWidth
            />
            <TextField
              label="Primary class/group"
              value={editingDeviceGroupName}
              onChange={(event) => setEditingDeviceGroupName(event.target.value)}
              fullWidth
            />
            <TextField
              label="Primary teacher"
              value={editingDeviceTeacherName}
              onChange={(event) => setEditingDeviceTeacherName(event.target.value)}
              fullWidth
            />
            <TextField
              label="All classes/sections"
              value={editingDeviceClassNames}
              onChange={(event) => setEditingDeviceClassNames(event.target.value)}
              helperText="Comma-separated classes used for schedule matching."
              fullWidth
            />
            <TextField
              label="All teachers"
              value={editingDeviceTeacherNames}
              onChange={(event) => setEditingDeviceTeacherNames(event.target.value)}
              helperText="Comma-separated teacher names used for schedule matching."
              fullWidth
            />
            <TextField
              select
              label="Extra profile layer"
              value={editingDeviceAssignedProfileId}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) =>
                  value
                    ? `District baseline + ${profiles.find((profile) => getItemId(profile) === value)?.profile_name || 'Policy profile'}`
                    : 'District baseline only',
              }}
              onChange={(event) => setEditingDeviceAssignedProfileId(event.target.value)}
              fullWidth
            >
              <MenuItem value="">District baseline only</MenuItem>
              {profiles.map((profile) => (
                <MenuItem key={getItemId(profile)} value={getItemId(profile)}>
                  {profile.profile_name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManagedDeviceEditor}>Cancel</Button>
          <Button onClick={handleSaveManagedDevice} variant="contained">
            Save device
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackForm open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Container>
  );
}

export default Dashboard;
